/**
 * Kunga Basics Admin — Internationalisation
 * Supports: English (en) · Français (fr) · Kinyarwanda (kin)
 * Default : en
 *
 * Usage (inside any component):
 *   const { t, lang, setLang } = useLang();
 *   <p>{t("nav.dashboard")}</p>
 */

import { createContext, useContext, useState } from "react";

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────

export const translations = {
  // ── ENGLISH ────────────────────────────────────────────────────────────────
  en: {
    // Common
    "common.save":      "Save Changes",
    "common.cancel":    "Cancel",
    "common.close":     "Close",
    "common.loading":   "Loading…",
    "common.search":    "Search…",
    "common.signOut":   "Sign Out",
    "common.next":      "Next",
    "common.prev":      "Prev",
    "common.saving":    "Saving…",
    "common.updating":  "Updating…",
    "common.sending":   "Sending…",
    "common.confirm":   "Confirm",
    "common.edit":      "Edit",
    "common.delete":    "Delete",

    // Login
    "login.brand":      "Kunga Basics",
    "login.subtitle":   "Admin Panel · v1.0",
    "login.email":      "Email",
    "login.password":   "Password",
    "login.signIn":     "Sign In →",
    "login.signingIn":  "Signing in…",
    "login.adminOnly":  "Admin access only",
    "login.otpTitle":    "Enter verification code",
    "login.otpSubtitle": "We sent a 6-digit code to {email}",
    "login.otpCode":     "Verification code",
    "login.verify":      "Verify →",
    "login.verifying":   "Verifying…",
    "login.resend":      "Resend code",
    "login.resending":   "Resending…",
    "login.resent":      "Code resent",
    "login.backToLogin": "← Back to login",

    // Nav sections
    "nav.overview":  "Overview",
    "nav.content":   "Content",
    "nav.users":     "Users",
    "nav.revenue":   "Revenue",

    // Nav items
    "nav.dashboard":     "Dashboard",
    "nav.analytics":     "Analytics",
    "nav.visitors":      "Visitor Analytics",
    "nav.modules":       "Modules",
    "nav.videos":        "Videos",
    "nav.announcements": "Announcements",
    "nav.allUsers":      "All Users",
    "nav.subscriptions": "Subscriptions",
    "nav.askGad":        "Ask Dr. Gad",
    "nav.assessments":   "Assessments",
    "nav.donations":     "Donations",
    "nav.mobileMoney":   "Mobile Money",
    "nav.cardPayments":  "Card Payments",
    "nav.manualPayments": "Manual Payments",
    "nav.transactions":  "Payment Transactions",
    "nav.pricing":       "Pricing",
    "nav.administration":   "Administration",
    "nav.supportTeam":      "Support Team",
    "nav.rolesPermissions": "Roles & Permissions",
    "nav.auditLogs":        "Audit Logs",
    "nav.countries":        "Country Availability",

    // ── Support Team ──────────────────────────────────────────────────
    "supportTeam.title":            "Support Team",
    "supportTeam.found":            "{n} members",
    "supportTeam.searchPlaceholder": "Search by name or email...",
    "supportTeam.newMember":        "New Member",
    "supportTeam.create":           "Create Member",
    "supportTeam.editMember":       "Edit Member",
    "supportTeam.name":             "Name",
    "supportTeam.email":            "Email",
    "supportTeam.phone":            "Phone",
    "supportTeam.roles":            "Roles",
    "supportTeam.directPermissions": "Direct Permission Overrides",
    "supportTeam.active":           "Active",
    "supportTeam.inactive":         "Inactive",
    "supportTeam.activate":         "Activate",
    "supportTeam.deactivate":       "Deactivate",
    "supportTeam.activated":        "Member activated",
    "supportTeam.deactivated":      "Member deactivated",
    "supportTeam.sendResetEmail":   "Send Password Reset",
    "supportTeam.resetEmailSent":   "Password reset email sent",
    "supportTeam.manage":           "Manage",
    "supportTeam.noneFound":        "No support team members found",
    "supportTeam.nameEmailRequired": "Name and email are required",
    "supportTeam.memberCreated":    "Member created — invite email sent",
    "supportTeam.memberUpdated":    "Member updated",
    "supportTeam.createFailed":     "Failed to create member",
    "supportTeam.updateFailed":     "Failed to update member",
    "supportTeam.credentialsTitle": "Member Created",
    "supportTeam.credentialsNote":  "Share this temporary password securely with the new member. It will not be shown again. A setup email has also been sent to their inbox.",
    "supportTeam.tempPassword":     "Temporary Password",
    "supportTeam.copy":             "Copy",
    "supportTeam.copied":           "Copied!",
    "supportTeam.done":             "Done",
    "supportTeam.regeneratePassword": "Regenerate Password",
    "supportTeam.confirmRegenerate": "Generate a new temporary password for this member? Their current password will stop working immediately.",
    "supportTeam.passwordRegenerated": "New temporary password generated",
    "supportTeam.regenerateFailed": "Failed to regenerate password",

    // ── Roles & Permissions ───────────────────────────────────────────
    "roles.title":              "Roles & Permissions",
    "roles.found":              "{n} roles",
    "roles.newRole":            "New Role",
    "roles.editRole":           "Edit Role",
    "roles.name":               "Name",
    "roles.description":        "Description",
    "roles.permissions":        "Permissions",
    "roles.permissionCount":    "Permissions",
    "roles.userCount":          "Users",
    "roles.system":             "System",
    "roles.all":                "All",
    "roles.noneFound":          "No roles found",
    "roles.nameRequired":       "Name is required",
    "roles.roleCreated":        "Role created",
    "roles.roleUpdated":        "Role updated",
    "roles.roleDeleted":        "Role deleted",
    "roles.saveFailed":         "Failed to save role",
    "roles.deleteFailed":       "Failed to delete role",
    "roles.cannotDeleteSystem": "System roles cannot be deleted",
    "roles.cannotDeleteAssigned": "Cannot delete a role with assigned users",
    "roles.confirmDelete":      "Delete role \"{name}\"? This cannot be undone.",
    "roles.usersInRole":        "Users with role \"{name}\"",
    "roles.noUsersAssigned":    "No users assigned to this role",
    "roles.tabRoles":           "Roles",
    "roles.tabPermissions":     "Permissions",
    "roles.searchPlaceholder":  "Search by role name...",
    "roles.status":             "Status",
    "roles.statusAll":          "All Statuses",
    "roles.statusActive":       "Active",
    "roles.statusInactive":     "Inactive",
    "roles.createdFrom":        "Created From",
    "roles.noResultsFiltered":  "No roles match your filters",

    // ── Permissions ────────────────────────────────────────────────────
    "permissions.title":            "Permissions",
    "permissions.found":            "{n} permissions",
    "permissions.searchPlaceholder": "Search by permission name or code...",
    "permissions.category":         "Category",
    "permissions.allCategories":    "All Categories",
    "permissions.code":             "Code",
    "permissions.description":      "Description",
    "permissions.noneFound":        "No permissions found",

    // Breadcrumbs
    "crumb.dashboard":    "Dashboard",
    "crumb.analytics":    "Analytics",
    "crumb.visitors":     "Visitor Analytics",
    "crumb.modules":      "Content / Modules",
    "crumb.moduleDetail": "Content / Module Detail",
    "crumb.videos":       "Content / Videos",
    "crumb.announcements":"Content / Announcements",
    "crumb.users":        "Users / All Users",
    "crumb.userDetail":   "Users / User Profile",
    "crumb.subscriptions":"Users / Subscriptions",
    "crumb.askgad":       "Users / Ask Dr. Gad Queue",
    "crumb.donations":    "Revenue / Donations",
    "crumb.mobilemoney":  "Revenue / Mobile Money",
    "crumb.cardpay":      "Revenue / Card Payments",
    "crumb.pricing":      "Revenue / Subscription Pricing",
    "crumb.account":      "Account Settings",

    // Search placeholders
    "search.dashboard":    "Search dashboard…",
    "search.analytics":    "Search analytics…",
    "search.visitors":     "Search visitor analytics…",
    "search.modules":      "Search modules…",
    "search.videos":       "Search videos…",
    "search.announcements":"Search announcements…",
    "search.users":        "Search users…",
    "search.subscriptions":"Search subscriptions…",
    "search.askgad":       "Search submissions…",
    "search.donations":    "Search donations…",
    "search.moduleDetail": "Search videos & resources…",
    "search.mobilemoney":  "Search transactions…",
    "search.cardpay":      "Search card payments…",
    "search.pricing":      "Search pricing…",
    "search.account":      "Search settings…",

    // Notifications panel
    "notif.title":       "Notifications",
    "notif.new":         "new",
    "notif.allCaughtUp": "All caught up!",
    "notif.noPending":   "No pending actions at the moment",
    "notif.tapToView":   "tap to view →",
    "notif.markAllRead": "Mark all read",
    "notif.close":       "Close",

    // Topbar / sidebar controls
    "topbar.broadcast":      "Broadcast",
    "topbar.accountSettings":"Account settings",
    "topbar.manageSidebar":  "Manage sidebar tabs",
    "sidebar.clickToHide":   "Click 👁 to hide/show tabs",
    "sidebar.resetAll":      "Reset — show all",
    "sidebar.collapse":      "Collapse sidebar",
    "sidebar.expand":        "Expand sidebar",
    "sidebar.showTab":       "Show tab",
    "sidebar.hideTab":       "Hide tab",

    // Account — tab labels
    "acct.profile":    "Profile",
    "acct.security":   "Security",
    "acct.appearance": "Appearance",
    "acct.session":    "Session",
    "acct.language":   "Language",

    // Account — Profile
    "acct.profileInfo":          "Profile Information",
    "acct.displayName":          "Display Name",
    "acct.emailAddress":         "Email Address",
    "acct.emailHint":            "Email cannot be changed from here.",
    "acct.adminRole":            "Administrator · Full access",
    "acct.changePhoto":          "Change Photo",
    "acct.uploading":            "Uploading {pct}%…",
    "acct.saveChanges":          "Save Changes",
    "acct.saving":               "Saving…",
    "acct.nameEmpty":            "Name cannot be empty",
    "acct.nameSaved":            "✅ Name updated!",

    // Account — Security
    "acct.changePassword":       "Change Password",
    "acct.pwHint":               "Choose a strong password of at least 8 characters. You will remain logged in after changing.",
    "acct.currentPw":            "Current Password",
    "acct.currentPwPlaceholder": "Enter your current password",
    "acct.newPw":                "New Password",
    "acct.newPwPlaceholder":     "At least 8 characters",
    "acct.confirmPw":            "Confirm New Password",
    "acct.confirmPwPlaceholder": "Repeat new password",
    "acct.pwMismatch":           "Passwords don't match",
    "acct.updatePw":             "Update Password",
    "acct.updating":             "Updating…",
    "acct.allFieldsRequired":    "All fields are required",
    "acct.pwNoMatch":            "New passwords do not match",
    "acct.pwTooShort":           "Password must be at least 8 characters",
    "acct.pwChanged":            "✅ Password changed!",

    // Account — Appearance
    "acct.colorTheme":    "Color Theme",
    "acct.themeHint":     "Choose how the admin panel looks. System follows your OS preference.",
    "acct.light":         "Light",
    "acct.dark":          "Dark",
    "acct.system":        "System",
    "acct.preview":       "Preview",
    "acct.sampleCard":    "Sample card",
    "acct.sampleCardHint":"This is how cards and text will appear in the selected theme.",
    "acct.active":        "Active",
    "acct.pending":       "Pending",

    // Account — Session
    "acct.sessionAccess":  "Session & Access",
    "acct.role":           "Role",
    "acct.roleHint":       "Your access level in the system",
    "acct.access":         "Access",
    "acct.fullAdmin":      "Full Admin",
    "acct.accessHint":     "Read + write access to all resources",
    "acct.authMethod":     "Auth Method",
    "acct.jwtBearer":      "JWT Bearer",
    "acct.authMethodHint": "Token-based authentication",
    "acct.status":         "Status",
    "acct.statusActive":   "Active",
    "acct.statusHint":     "Session is currently valid",

    // Account — Language
    "acct.languageTitle":  "Language / Langue / Ururimi",
    "acct.languageHint":   "Choose the display language for the admin panel interface.",
    "acct.languageSaved":  "✅ Language updated!",
    "acct.langCurrent":    "Current language",

    // Language names (always shown in native script)
    "lang.en":  "English",
    "lang.fr":  "Français",
    "lang.kin": "Kinyarwanda",

    // Sign-out confirmation
    "signout.title":   "Sign out?",
    "signout.message": "You'll be redirected to the login screen.",
    "signout.cancel":  "Cancel",
    "signout.confirm": "Sign Out",

    // Session expiry toast
    "session.expired": "⚠️ Your session has expired. Please sign in again.",

    // ── Table column headers (reusable) ──────────────────────────────────────
    "tbl.user":      "User",
    "tbl.status":    "Status",
    "tbl.plan":      "Plan",
    "tbl.platform":  "Platform",
    "tbl.expires":   "Expires",
    "tbl.child":     "Child",
    "tbl.date":      "Date",
    "tbl.amount":    "Amount",
    "tbl.joined":    "Joined",
    "tbl.action":    "Action",
    "tbl.method":    "Method",
    "tbl.campaign":  "Campaign",
    "tbl.donor":     "Donor",
    "tbl.name":      "Name",

    // ── Reusable UI strings ──────────────────────────────────────────────────
    "ui.backToList":    "← Back to list",
    "ui.clickForDetails": "click row for details",
    "ui.never":         "Never",
    "ui.noData":        "No data",
    "ui.showingOf":     "Showing {n} of {total}",
    "ui.anonymous":     "Anonymous",
    "ui.viewIn":        "View in {page} →",
    "ui.goTo":          "Go to {page} →",
    "ui.retry":         "↺ Retry",
    "ui.page":          "Page {n} of {total}",
    "ui.clickToView":   "click to view →",
    "ui.clickToFilter": "click to filter",
    "ui.active":        "● active",

    // ── Dashboard ────────────────────────────────────────────────────────────
    "dash.activeSubscribers":       "Active Subscribers",
    "dash.monthlyRevenue":          "Monthly Revenue",
    "dash.gadQueue":                "Dr. Gad Queue",
    "dash.totalDonations":          "Total Donations",
    "dash.revenueHealth":           "Revenue Health",
    "dash.fullBreakdown":           "Full breakdown →",
    "dash.liveActivity":            "Live Activity",
    "dash.noActivity":              "No activity yet",
    "dash.recentlyActive":          "Recently Active Users",
    "dash.moduleCompletion":        "Module Completion Rates",
    "dash.weeklySignups":           "Weekly Signups & Active Users",
    "dash.clickToView":             "click to view →",
    "dash.clickBarForDetails":      "click a bar for details",
    "dash.noSignupData":            "No signup data yet",
    "dash.newSignups":              "New Signups",
    "dash.activeUsers":             "Active Users",
    "dash.peakSignups":             "Peak signups",
    "dash.peakActive":              "Peak active",
    "dash.totalSignups":            "Total signups",
    "dash.liveFromDb":              "Live from database",
    "dash.mrr":                     "MRR",
    "dash.subscribers":             "Subscribers",
    "dash.churnRate":               "Churn Rate",
    "dash.conversion":              "Conversion",
    "dash.trialUsers":              "Trial Users",
    "dash.scholarships":            "Scholarships",
    "dash.subscriptionStatus":      "Subscription Status",
    "dash.subscriptionExpires":     "Subscription Expires",
    "dash.viewInSubscriptions":     "View in Subscriptions →",
    "dash.allActiveSubscribers":    "All active subscribers",
    "dash.goToSubscriptions":       "Go to Subscriptions →",
    "dash.monthlyRevenueBreakdown": "Monthly Revenue Breakdown",
    "dash.monthlyRecurring":        "Monthly Recurring Revenue",
    "dash.activeSubs":              "Active Subs",
    "dash.hoverForStats":           "Hover for stats · click bar to drill in",
    "dash.byPlatform":              "By Platform",
    "dash.backToRevenue":           "← Back to Revenue",
    "dash.share":                   "Share",
    "dash.gadQueueTitle":           "Dr. Gad Queue",
    "dash.backToQueue":             "← Back to queue",
    "dash.submitted":               "Submitted",
    "dash.responseSent":            "✅ Response sent",
    "dash.responseVideo":           "Response video",
    "dash.writeResponse":           "Write Response",
    "dash.responseTextLabel":       "Response Text *",
    "dash.responseVideoUrl":        "Response Video URL (optional)",
    "dash.sendResponse":            "✅ Send Response",
    "dash.respondToQuestion":       "✏️ Respond to this question",
    "dash.openInAskGad":            "Open in Ask Dr. Gad →",
    "dash.noSubmissions":           "No submissions in this category",
    "dash.goToAskGad":              "Go to Ask Dr. Gad →",
    "dash.donationsOverview":       "Donations Overview",
    "dash.donationAmount":          "Donation Amount",
    "dash.totalReceived":           "Total Received",
    "dash.goToDonations":           "Go to Donations →",
    "dash.viewInDonations":         "View in Donations →",
    "dash.activityDetail":          "Activity Detail",
    "dash.userId":                  "User ID",
    "dash.adminId":                 "Admin ID",
    "dash.completions":             "Completions",
    "dash.completionRate":          "Completion rate",
    "dash.childAge":                "Child Age",
    "dash.weekSignups":             "Week {week} Signups",
    "dash.period":                  "Period",
    "dash.signups":                 "Signups",
    "dash.ipAddress":               "IP Address",
    "dash.osPlatform":              "OS / Platform",
    "dash.lastLogin":               "Last Login",
    "dash.userDetails":             "User Details",
    "dash.last6":                   "last 6 logged-in · click for details",
    "dash.activeSubscribersModal":  "Active Subscribers",
    "dash.totalActive":             "Total Active",
    "dash.completionRateLabel":     "Completion rate",

    // ── Analytics ────────────────────────────────────────────────────────────
    "analytics.dau":               "DAU",
    "analytics.mau":               "MAU",
    "analytics.stickiness":        "Stickiness",
    "analytics.avgSession":        "Avg Session",
    "analytics.dailyActive":       "Daily Active Users",
    "analytics.monthlyActive":     "Monthly Active Users",
    "analytics.dauMauRatio":       "DAU / MAU ratio",
    "analytics.sessionLength":     "Session length",
    "analytics.subscriptionFunnel":"Subscription Funnel",
    "analytics.revenueByPlatform": "Revenue by Platform",
    "analytics.platformPerformance":"Platform Performance",
    "analytics.mobileMoneyProviders":"Mobile Money Providers",
    "analytics.retentionSummary":  "Retention Summary",
    "analytics.retentionCohorts":  "Retention Cohorts",
    "analytics.noRetentionData":   "No Retention Data Yet",
    "analytics.connectPostHog":    "Connect PostHog to track cohort retention. Once connected, weekly cohorts will appear here automatically.",
    "analytics.latestCohort":      "Latest Cohort",
    "analytics.day1Retention":     "Day 1 Retention",
    "analytics.day7Retention":     "Day 7 Retention",
    "analytics.avg30dRetention":   "Avg 30d Retention",
    "analytics.cohortSize":        "Cohort Size",
    "analytics.retentionCurve":    "Retention Curve",
    "analytics.allCohortsComparison":"All Cohorts Comparison",
    "analytics.funnel":            "Funnel: {step}",
    "analytics.fullFunnel":        "Full Funnel",
    "analytics.matchingUsers":     "Matching Users ({n})",
    "analytics.allPlatformsBreakdown":"All Platforms Breakdown",
    "analytics.pctShare":          "% share",
    "analytics.cohort":            "Cohort: {cohort}",
    "analytics.dropped":           "▼ {pct}% dropped",
    "analytics.liveFromDb":        "Live from DB",
    "analytics.activeSubscribers": "Active subscribers",
    "analytics.targetStickiness":  "🔥 Target: 20%+",
    "analytics.postHog":           "PostHog integration",
    "analytics.clickForDetails":   "click row for details",
    "analytics.clickSegment":      "click segment for details",
    "analytics.activeBySource":    "active subscribers by payment source",
    "analytics.cohortsTracked":    "{n} cohorts tracked",
    "analytics.cohortsConnectHint":"Connect PostHog to track cohort retention.",
    "analytics.usersInCohort":     "users in this cohort",
    "analytics.30dayRetention":    "30-Day Retention",
    "analytics.notEnoughData":     "not enough data",
    "analytics.excellentRetention":"🔥 Excellent retention",
    "analytics.needsImprovement":  "⚠️ Needs improvement",
    "analytics.highChurnRisk":     "🔴 High churn risk",
    "analytics.activeMobileMoneySubscribers": "{n} active mobile money subscribers",
    "analytics.pending":           "Pending",

    // ── Users page ───────────────────────────────────────────────────────────
    "users.title":           "User Management",
    "users.found":           "{n} users found",
    "users.searchPlaceholder":"Search by name or email…",
    "users.allStatus":       "All Status",
    "users.active":          "🟢 Active",
    "users.free":            "⚪ Free / None",
    "users.cancelled":       "🔴 Cancelled",
    "users.expired":         "🟠 Expired",
    "users.clear":           "Clear",
    "users.sendEmail":       "Send Email",
    "users.view":            "View",
    "users.allUsers":        "All Users",
    "users.failedToLoad":    "Failed to load user",
    "users.userNotFound":    "User not found",
    "users.modulesCompleted":"Modules Completed",
    "users.inProgress":      "In Progress",
    "users.journalEntries":  "Journal Entries",
    "users.gadQuestions":    "Dr. Gad Questions",
    "users.donations":       "Donations",
    "users.accountInfo":     "Account Info",
    "users.subscription":    "Subscription",
    "users.childProfile":    "Child Profile",
    "users.appPreferences":  "App Preferences",
    "users.moduleProgress":  "Module Progress ({n} modules)",
    "users.recentActivity":  "Recent Activity (last 20 events)",
    "users.gadSubmissions":  "Ask Dr. Gad Submissions ({n})",
    "users.donationHistory": "Donation History ({n})",
    "users.noActiveSubscription": "No active subscription",
    "users.pushActive":      "Push notifications active",
    "users.emailSubject":    "Subject",
    "users.emailMessage":    "Message",
    "users.sendEmailBtn":    "Send Email",
    "users.preparing":       "Preparing {fmt}…",
    "users.joined":          "Joined",
    "users.lastLogin":       "Last Login",
    "users.focusAreas":      "Focus areas / challenges",
    "users.done":            "Done",
    "users.inProgressStatus":"In progress",
    "users.noUsersFound":    "No users found",
    "users.noUsersMatching": "No users matching \"{q}\"",
    "users.pageOf":          "Page {page} of {totalPages} · {total} total",
    "users.emailPlaceholder":"Email subject…",
    "users.messagePlaceholder":"Write your message…",
    "users.sendEmailTitle":  "✉️ Send Email",
    "users.emailRequired":   "Subject and message are required",
    "users.emailSent":       "✅ Email sent to {email}",
    "users.emailFailed":     "Failed: {err}",
    "users.role":            "Role",
    "users.pushToken":       "Push Token",
    "users.notConnected":    "Not connected",
    "users.activeToken":     "Active ✓",
    "users.renews":          "Renews",
    "users.revenuecatId":    "RevenueCat ID",
    "users.childName":       "Child Name",
    "users.age":             "Age",
    "users.ageMonths":       "{n} months",
    "users.dateOfBirth":     "Date of Birth",
    "users.notifications":   "Notifications",
    "users.routineReminders":"Routine Reminders",
    "users.on":              "On",
    "users.off":             "Off",
    "users.darkMode":        "Dark Mode",
    "users.textSize":        "Text Size",
    "users.langPref":        "Language",
    "users.marketingEmails": "Marketing Emails",
    "users.optedIn":         "Opted in",
    "users.optedOut":        "Opted out",

    // ── Modules ──────────────────────────────────────────────────────────────
    "mods.title":            "Module Management",
    "mods.createModule":     "Create Module",
    "mods.manageGroups":     "Manage Groups",
    "mods.published":        "Published",
    "mods.draft":            "Draft",
    "mods.groups":           "Groups",
    "mods.totalModules":     "Total Modules",
    "mods.allModules":       "all modules",
    "mods.liveForSubscribers":"live for subscribers",
    "mods.unpublishedModules":"unpublished modules",
    "mods.contentCategories":"content categories",
    "mods.clickToManage":    "click to manage",
    "mods.clickToReset":     "click to reset",
    "mods.noModulesMatch":   "No modules match your filters",
    "mods.freePreview":      "Free Preview",
    "mods.completions":      "Completions",
    "mods.feedback":         "Feedback",
    "mods.videos":           "Videos",
    "mods.sortOrder":        "Sort Order",
    "mods.preview":          "Preview",
    "mods.createdAt":        "Created",
    "mods.nReviews":         "{n} reviews",
    "mods.freePreviewCheck": "✅ Free preview",
    "mods.subscribersOnly":  "🔒 Subscribers only",
    "mods.unpublish":        "⏸ Unpublish",
    "mods.publishModule":    "▶ Publish Module",
    "mods.publish":          "▶ Publish",
    "mods.manageResources":  "📎 Resources",
    "mods.editResource":     "Edit Resource",
    "mods.addNewResource":   "Add New Resource",
    "mods.storageMethod":    "Storage Method",
    "mods.resourceType":     "Resource Type",
    "mods.resourceTitle":    "Title",
    "mods.resourceDesc":     "Description",
    "mods.fileSize":         "File Size",
    "mods.pageCount":        "Page Count",
    "mods.visibility":       "Visibility",
    "mods.addResource":      "Add Resource",
    "mods.noResourcesYet":   "No resources yet",
    "mods.uploading":        "Uploading…",
    "mods.createNewModule":  "📚 Create New Module",
    "mods.moduleCode":       "Module Code",
    "mods.groupLabel":       "Group",
    "mods.selectGroup":      "Select group…",
    "mods.titleLabel":       "Title",
    "mods.descLabel":        "Description",
    "mods.whatToExpect":     "What to Expect",
    "mods.freePreviewLabel": "Free preview module (accessible without subscription)",
    "mods.creating":         "Creating…",
    "mods.editModule":       "✏️ Edit Module",
    "mods.archived":         "Archived",
    "mods.moduleGroups":     "📂 Module Groups",
    "mods.editGroup":        "Edit Group",
    "mods.newGroup":         "New Group",
    "mods.emoji":            "Emoji",
    "mods.groupName":        "Group Name",
    "mods.createGroup":      "✅ Create Group",
    "mods.groupCount":       "{n} groups",
    "mods.noGroupsYet":      "No groups yet — create one above",
    "mods.done":             "Done",
    "mods.deleteGroup":      "🗑 Delete Group",
    "mods.deleteGroupConfirm":"Delete \"{name}\"? This only works if the group has no modules. This cannot be undone.",
    "mods.deleting":         "Deleting…",
    "mods.moduleCount":      "{n} modules",
    "mods.code":             "Code",
    "mods.backToModules":    "← Modules",
    "mods.failedToLoad":     "Failed to load module",
    "mods.completionsCTA":   "learners completed",
    "mods.avgRating":        "Avg Rating",
    "mods.outOf50":          "out of 5.0",
    "mods.attachments":      "attachments",
    "mods.videosResources":  "Videos & Resources",
    "mods.editResources":    "✏️ Edit Resources",
    "mods.noVideosYet":      "No videos yet",
    "mods.totalVideos":      "total videos",

    // ── Videos ───────────────────────────────────────────────────────────────
    "videos.title":          "Video Management",
    "videos.addVideo":       "Add Video",
    "videos.allVideos":      "All Videos",
    "videos.noVideosMatch":  "No videos match your filters",
    "videos.createVideo":    "Create Video",
    "videos.editVideo":      "Edit Video",
    "videos.details":        "🎬 Video Details",
    "videos.videoEnded":     "Video ended",
    "videos.replay":         "▶ Replay",
    "videos.module":         "📦 Module",
    "videos.duration":       "⏱ Duration",
    "videos.added":          "📅 Added",
    "videos.idLabel":        "🆔 ID",
    "videos.publish":        "📣 Publish",
    "videos.unpublish":      "📝 Unpublish",
    "videos.editBtn":        "✏️ Edit",
    "videos.archive":        "🗑 Archive",
    "videos.unknown":        "Unknown",
    "videos.noVideosYet":    "No videos yet",

    // ── Announcements ────────────────────────────────────────────────────────
    "anns.title":            "Create Announcement",
    "anns.announcementTitle":"Title",
    "anns.message":          "Message",
    "anns.targetAudience":   "Target Audience",
    "anns.saveDraft":        "💾 Save Draft",
    "anns.publishNow":       "📣 Publish Now",
    "anns.confirmPublish":   "📣 Confirm Publish",
    "anns.yesPublish":       "📣 Yes, Publish Now",
    "anns.statistics":       "Announcement Statistics",
    "anns.livePreview":      "📱 Live Preview",
    "anns.totalSent":        "Total Sent",
    "anns.pushDelivered":    "Push Delivered",
    "anns.bannerViews":      "Banner Views",
    "anns.dismissals":       "Dismissals",
    "anns.noAnnouncements":  "No announcements yet",
    "anns.clickToViewWhoSaw":"Click any row to see who viewed that announcement",
    "anns.details":          "📣 Announcement Details",
    "anns.pushSent":         "Push Sent",
    "anns.dismissed":        "Dismissed",
    "anns.openRate":         "Open Rate",
    "anns.created":          "Created",
    "anns.publishedDate":    "Published",
    "anns.draftOnly":        "Draft only",
    "anns.whoSawThis":       "Who Saw This",
    "anns.whoViewed":        "👁 Who Viewed",
    "anns.noViewData":       "No view data yet",
    "anns.viewedAt":         "Viewed At",
    "anns.sentTitle":        "📣 Announcements Sent",
    "anns.bannerViewsTitle": "👁 Banner Views",
    "anns.pushDeliveredTitle":"📊 Push Delivered",
    "anns.dismissalsTitle":  "✅ Dismissals",
    "anns.audience":         "Audience",
    "anns.dismissedBy":      "Dismissed By",
    "anns.sentDesc":         "All published announcements and their push notification reach.",
    "anns.bannerDesc":       "Announcements and how many times the in-app banner was viewed.",
    "anns.pushDesc":         "Total push notifications delivered per announcement.",
    "anns.dismissalsDesc":   "Users who tapped or dismissed the in-app banner.",

    // ── Subscriptions ────────────────────────────────────────────────────────
    "subs.title":            "Subscriptions ({n})",
    "subs.allPlatforms":     "All Platforms",
    "subs.allStatus":        "All Status",
    "subs.allPlans":         "All Plans",
    "subs.noMatch":          "No subscriptions match your filters",
    "subs.restore":          "Restore",
    "subs.cancel":           "Cancel",
    "subs.filtering":        "● filtering",
    "subs.clickToFilter":    "click to filter",
    "subs.details":          "💳 Subscription Details",
    "subs.periodStart":      "Period Start",
    "subs.periodEnd":        "Period End",
    "subs.cancelledAt":      "Cancelled At",
    "subs.mobileProvider":   "Mobile Provider",
    "subs.mobilePhone":      "Mobile Phone",
    "subs.restoreBtn":       "✅ Restore Subscription",
    "subs.cancelBtn":        "Cancel Subscription",

    // ── Ask Dr. Gad ──────────────────────────────────────────────────────────
    "askgad.pending":        "Pending",
    "askgad.underReview":    "Under Review",
    "askgad.responded":      "Responded",
    "askgad.total":          "Total",
    "askgad.allSubmissions": "all submissions",
    "askgad.awaitingResponse":"awaiting response",
    "askgad.beingReviewed":  "being reviewed",
    "askgad.completed":      "completed",
    "askgad.noMatch":        "No submissions match your filters",
    "askgad.submissionDetails":"🎤 Submission Details",
    "askgad.respondTitle":   "✍️ Respond to Question",
    "askgad.writeResponse":  "Write Response",
    "askgad.yourResponse":   "Your Response",
    "askgad.pushNoteSent":   "(will be saved + push notification sent)",
    "askgad.sendResponse":   "Send Response + Push Notification",
    "askgad.drGadResponse":  "Dr. Gad's Response",
    "askgad.writeResponseBtn":"✍️ Write Response",
    "askgad.details":        "Details",
    "askgad.respond":        "✍️ Respond",

    // ── Donations ────────────────────────────────────────────────────────────
    "donations.title":           "Donations ({n})",
    "donations.totalReceived":   "Total Received",
    "donations.scholarships":    "Scholarships",
    "donations.pending":         "Pending",
    "donations.methods":         "Methods",
    "donations.noYet":           "No donations yet",
    "donations.details":         "💝 Donation Details",
    "donations.scholarshipDetails":"🎓 Scholarship Details",
    "donations.donorWall":       "Donor Wall",
    "donations.visible":         "✅ Visible",
    "donations.private":         "🔒 Private",
    "donations.scholarshipGranted":"Scholarship Access Granted",
    "donations.grantedOn":       "Granted On",
    "donations.notes":           "Notes",
    "donations.userId":          "User ID",
    "donations.activeLabel":     "Active",
    "donations.email":           "Email",
    "donations.activeScholarships":"Active Scholarships",
    "donations.donationsByMethod":"💳 Donations by Method",
    "donations.confirmGrant":    "🎓 Confirm Scholarship Grant",
    "donations.grantByEmail":    "Grant by Email",
    "donations.grant":           "Grant",
    "donations.confirmGrantBtn": "✅ Confirm Grant",
    "donations.totalReceivedLabel":"Total Received",

    // ── Mobile Money ─────────────────────────────────────────────────────────
    "mm.title":              "Transactions ({n})",
    "mm.providerBreakdown":  "Provider Breakdown",
    "mm.total":              "Total",
    "mm.active":             "Active",
    "mm.pending":            "Pending",
    "mm.failed":             "Failed",
    "mm.allProviders":       "All Providers",
    "mm.allStatus":          "All Status",
    "mm.allPlans":           "All Plans",
    "mm.noMatch":            "No transactions match your filters",
    "mm.transactionDetails": "📱 Transaction Details",
    "mm.activate":           "⚡ Activate",
    "mm.manualActivate":     "⚡ Manually Activate Subscription",
    "mm.provider":           "Provider",
    "mm.phone":              "Phone",
    "mm.txRef":              "Tx Ref",
    "mm.userEmail":          "User Email",

    // ── Table headers ────────────────────────────────────────────────────────
    "tbl.title":             "Title",
    "tbl.type":              "Type",
    "tbl.source":            "Source",
    "tbl.duration":          "Duration",
    "tbl.added":             "Added",
    "tbl.actions":           "Actions",
    "tbl.storage":           "Storage",
    "tbl.description":       "Description",
    "tbl.code":              "Code",
    "tbl.videos":            "Videos",
    "tbl.preview":           "Preview",
    "tbl.completions":       "Completions",

    // ── Pricing ──────────────────────────────────────────────────────────────
    "pricing.title":         "Subscription Pricing",
    "pricing.subtitle":      "Manage subscription prices, trial duration and payment integration settings",
    "pricing.monthlyPlan":   "Monthly Plan",
    "pricing.annualPlan":    "Annual Plan",
    "pricing.freeTrial":     "Free Trial",
    "pricing.localCurrency": "Local Currency",
    "pricing.localCountry":  "Local Country",
    "pricing.refresh":       "Refresh",
    "pricing.retry":         "Retry",
    "pricing.notSet":        "not set",
    "pricing.editBtn":       "✏️ Edit",
    "pricing.editTitle":     "✏️ Edit {label}",
    "pricing.saveBtn":       "✅ Save",

    // ── Card Payments ────────────────────────────────────────────────────────
    "cardpay.title":         "Card Payments — Stripe",
    "cardpay.subtitle":      "Stripe card payment subscriptions and webhook event handlers",
    "cardpay.total":         "Total",
    "cardpay.active":        "Active",
    "cardpay.trial":         "Trial",
    "cardpay.cancelled":     "Cancelled",
    "cardpay.webhookHandlers":"Stripe Webhook Handlers",
    "cardpay.allActive":     "All active",
    "cardpay.stripePaymentDetail":"Stripe Payment Detail",
    "cardpay.stripeSubscriptions":"Stripe Subscriptions ({n})",
    "cardpay.noStripeFound": "No Stripe subscriptions found",
    "cardpay.allStripeSubs": "all Stripe subs",
    "cardpay.payingSubscribers":"paying subscribers",
    "cardpay.inFreeTrial":   "in free trial",
    "cardpay.churned":       "churned",
    "cardpay.stripeSubId":   "Stripe Sub ID",
    "cardpay.stripeCustomer":"Stripe Customer",

    // ── Status display labels ────────────────────────────────────────────────
    "status.active":       "Active",
    "status.published":    "Published",
    "status.draft":        "Draft",
    "status.archived":     "Archived",
    "status.trial":        "Trial",
    "status.cancelled":    "Cancelled",
    "status.expired":      "Expired",
    "status.pending":      "Pending",
    "status.scholarship":  "Scholarship",
    "status.completed":    "Completed",
    "status.failed":       "Failed",
    "status.responded":    "Responded",
    "status.none":         "None",

    // ── Common filter ────────────────────────────────────────────────────────
    "filter.all":          "All",

    // ── Subscription filter / select options ─────────────────────────────────
    "subs.statusActive":      "✅ Active",
    "subs.statusTrial":       "🟡 Trial",
    "subs.statusCancelled":   "❌ Cancelled",
    "subs.statusExpired":     "🕐 Expired",
    "subs.statusScholarship": "🎓 Scholarship",
    "subs.planMonthly":       "Monthly",
    "subs.planAnnual":        "Annual",
    "subs.planScholarship":   "Scholarship",
    "subs.childProfile":      "🧒 Child Profile",

    // ── Ask Dr. Gad filter / status labels ───────────────────────────────────
    "askgad.filterReview":        "🔍 Review",
    "askgad.filterDone":          "✅ Done",
    "askgad.statusSubmitted":     "⏳ Pending",
    "askgad.statusUnderReview":   "🔍 Under Review",
    "askgad.statusResponded":     "✅ Responded",
    "askgad.statusEscalated":     "🚨 Escalated",
    "askgad.responsePlaceholder": "Write your detailed response to this parent…",
    "askgad.assignTo":            "Assign to",
    "askgad.assigned":            "Assigned to",
    "askgad.unassigned":          "Unassigned",
    "askgad.assignSuccess":       "Submission assigned",
    "askgad.escalate":            "Escalate",
    "askgad.escalateTitle":       "🚨 Escalate Question",
    "askgad.escalationReason":    "Escalation Reason",
    "askgad.escalateSuccess":     "Submission escalated to Super Admins",
    "askgad.escalatedBy":         "Escalated by",
    "askgad.myQueue":             "My Queue",

    // ── Audit Log ─────────────────────────────────────────────────────────────
    "auditLog.title":            "Audit Log",
    "auditLog.found":            "{n} entries found",
    "auditLog.searchPlaceholder":"Search by action or module…",
    "auditLog.allModules":       "All Modules",
    "auditLog.details":          "Details",
    "auditLog.previousValue":    "Previous Value",
    "auditLog.newValue":         "New Value",
    "auditLog.system":           "System",
    "auditLog.noneFound":        "No audit log entries found",
    "auditLog.dateTime":         "Date & Time",
    "auditLog.module":           "Module",
    "auditLog.action":           "Action",
    "auditLog.entityType":       "Entity Type",
    "auditLog.entityId":         "Entity ID",
    "auditLog.ipAddress":        "IP Address",
    "auditLog.browser":          "Browser",
    "auditLog.device":           "Device",
    "auditLog.sessionId":        "Session ID",
    "auditLog.manageColumns":    "Manage Columns",
    "auditLog.notAvailable":     "Not available",

    "assessments.found":              "{n} assessments found",
    "assessments.searchPlaceholder":  "Search by child, parent or email…",
    "assessments.noneFound":          "No assessments found",
    "assessments.dateTime":           "Date & Time",
    "assessments.child":              "Child",
    "assessments.overall":            "Overall Score",
    "assessments.recommendedProgram": "Recommended Program",
    "assessments.viewDetails":        "View Details",
    "assessments.parent":             "Parent / Guardian",
    "assessments.dob":                "Date of Birth",
    "assessments.gender":             "Gender",
    "assessments.country":            "Country",
    "assessments.domainScores":       "Domain Scores",
    "assessments.strengths":          "Strengths",
    "assessments.areasToSupport":     "Areas to Support",
    "assessments.uploads":            "Uploaded Files",
    "assessments.videos":             "videos",
    "assessments.photos":             "photos",
    "assessments.reports":            "reports",

    // ── Modules filter options ────────────────────────────────────────────────
    "mods.allStatus":       "All Status",
    "mods.statusPublished": "✅ Published",
    "mods.statusDraft":     "📝 Draft",
    "mods.statusArchived":  "🗃 Archived",

    // ── Video type options ────────────────────────────────────────────────────
    "videos.allTypes":          "All Types",
    "videos.typeIntro":         "Intro",
    "videos.typePractice":      "Practice",
    "videos.typeContent":       "Content",
    "videos.typeExplanation":   "Explanation",
    "videos.typeTaster":        "Taster",
    "videos.typeDemonstration": "Demonstration",
    "videos.typeAssessment":    "Assessment",
    "videos.selectModule":      "Select module…",
    "videos.selectModuleDash":  "— Select module —",
    "videos.previewClipLabel":  "Free preview clip",
    "videos.ytFetching":        "Auto-filling from YouTube…",
    "videos.titlePlaceholder":  "e.g. Introduction to Speech Development",

    // ── Announcements live preview & form ─────────────────────────────────────
    "anns.previewNow":           "now",
    "anns.titleInputPh":         "Announcement title…",
    "anns.messageInputPh":       "Parents will see this as a push notification and in-app banner…",
    "anns.previewTitlePh":       "Your announcement title",
    "anns.previewBodyPh":        "Your message will appear here…",
    "anns.inAppBanner":          "In-App Banner",
    "anns.bannerTitlePh":        "Title",
    "anns.bannerBodyPh":         "Message preview",
    "anns.target":               "Target",
    "anns.viewersHeader":        "Viewers",
    "anns.targetAllSubscribers": "All Subscribers",
    "anns.targetActiveOnly":     "Active Only",
    "anns.targetSpeechModule":   "Speech Module Users",
    "anns.targetAllRegistered":  "All Registered",
    "anns.subAnnouncements":     "announcements created",
    "anns.subPushNotifs":        "total push notifications",
    "anns.subBannerViews":       "in-app banner views",
    "anns.subConfirmedViews":    "confirmed banner views",
    "anns.publishWarning":       "This will immediately send a push notification to all matching users. This action cannot be undone.",
    "anns.confirmTarget":        "Target: {target}",
    "anns.titleRequired":        "Title and body are required",
    "anns.draftSaved":           "✅ Draft saved!",
    "anns.publishedToast":       "📣 Announcement published!",

    // ── Donations page strings ────────────────────────────────────────────────
    "donations.donationsCardTitle":"Donations ({n})",
    "donations.scholarshipsTitle": "Scholarships ({n})",
    "donations.filterAll":         "All",
    "donations.filterCompleted":   "Completed",
    "donations.filterPending":     "Pending",
    "donations.filterFailed":      "Failed",
    "donations.noScholarshipsYet": "No scholarships yet",
    "donations.noData":            "No data",
    "donations.activeBadge":       "active",
    "donations.grantedLabel":      "Granted {date}",
    "donations.scholarshipsCount": "{n} active scholarship grants — full access granted free of charge.",
    "donations.donationsCount":    "{n} donations",

    // ── Pricing labels, descriptions & groups ─────────────────────────────────
    "pricing.labelGoldMonthly":      "Gold — Monthly (USD)",
    "pricing.descGoldMonthly":       "Gold tier monthly price (USD)",
    "pricing.labelGoldQuarterly":    "Gold — 3 Months (USD)",
    "pricing.descGoldQuarterly":     "Gold tier 3-month price (USD)",
    "pricing.labelGoldAnnual":       "Gold — Annual (USD)",
    "pricing.descGoldAnnual":        "Gold tier annual price (USD)",
    "pricing.labelPremiumMonthly":   "Premium — Monthly (USD)",
    "pricing.descPremiumMonthly":    "Premium tier monthly price (USD)",
    "pricing.labelPremiumQuarterly": "Premium — 3 Months (USD)",
    "pricing.descPremiumQuarterly":  "Premium tier 3-month price (USD)",
    "pricing.labelPremiumAnnual":    "Premium — Annual (USD)",
    "pricing.descPremiumAnnual":     "Premium tier annual price (USD)",
    "pricing.groupGold":             "Gold Tier Pricing",
    "pricing.groupPremium":          "Premium Tier Pricing",
    "pricing.labelMonthlyUsd":   "Monthly Price (USD)",
    "pricing.descMonthlyUsd":    "Monthly subscription price (USD)",
    "pricing.labelAnnualUsd":    "Annual Price (USD)",
    "pricing.descAnnualUsd":     "Annual subscription price (USD)",
    "pricing.labelMonthlyLocal": "Monthly Price (Local)",
    "pricing.descMonthlyLocal":  "Monthly price in local currency (optional display label)",
    "pricing.labelAnnualLocal":  "Annual Price (Local)",
    "pricing.descAnnualLocal":   "Annual price in local currency (optional display label)",
    "pricing.labelCurrencyLocal":"Local Currency Code",
    "pricing.descCurrencyLocal": "ISO currency code (e.g. KES, NGN)",
    "pricing.labelLocalCountry": "Local Country",
    "pricing.descLocalCountry":  "Primary market country",
    "pricing.labelTrialDays":    "Free Trial Days",
    "pricing.descTrialDays":     "Days of free access before billing starts",
    "pricing.labelStripeMonthly":"Stripe Monthly Price ID",
    "pricing.descStripeMonthly": "Stripe Price ID for monthly plan",
    "pricing.labelStripeAnnual": "Stripe Annual Price ID",
    "pricing.descStripeAnnual":  "Stripe Price ID for annual plan",
    "pricing.groupSubscription": "Subscription Pricing",
    "pricing.groupTrial":        "Trial Settings",
    "pricing.groupStripe":       "Stripe Integration",
    "pricing.currency":          "Currency: {code}",
    "pricing.updated":           "Updated {date}",
    "pricing.priceHint":         "💡 Enter numeric value only (e.g. 14.00 for $14). Currency symbol is not needed.",
    "pricing.thenCharged":       "Then charged monthly",
    "pricing.usdStripe":         "USD · Stripe / Flutterwave",

    // ── Account — MFA / 2FA ──────────────────────────────────────────────────
    "acct.mfa":             "Two-Factor Authentication (2FA)",
    "acct.mfaHint":         "Add an extra layer of security. When enabled, you'll receive a one-time code each time you sign in.",
    "acct.mfaEnabled":      "2FA is enabled on your account",
    "acct.mfaDisabled":     "2FA is currently disabled",
    "acct.enableMfa":       "Enable 2FA",
    "acct.disableMfa":      "Disable 2FA",
    "acct.sendOtp":         "Send Code",
    "acct.sendingOtp":      "Sending…",
    "acct.otpCode":         "Verification Code",
    "acct.otpPlaceholder":  "Enter 6-digit code",
    "acct.verifyOtp":       "Verify & Enable",
    "acct.verifyingOtp":    "Verifying…",
    "acct.otpSent":         "✅ Code sent to {dest}",
    "acct.mfaEnabledToast": "✅ Two-factor authentication enabled!",
    "acct.mfaDisabledToast":"2FA has been disabled",

    // ── LEGAL PAGES ─────────────────────────────────────────────────────────

    // Crumbs + search
    "crumb.privacy":  "Privacy Policy",
    "crumb.terms":    "Terms of Service",
    "crumb.support":  "Support Center",
    "search.privacy": "Search privacy policy…",
    "search.terms":   "Search terms…",
    "search.support": "Search support…",

    // Footer link labels
    "footer.privacy": "Privacy Policy",
    "footer.terms":   "Terms of Service",
    "footer.support": "Support",

    // Shared badge labels
    "legal.lastUpdated":   "Last Updated",
    "legal.jurisdiction":  "Jurisdiction",
    "legal.dataStandard":  "Data Standard",
    "legal.effectiveDate": "Effective Date",
    "legal.governingLaw":  "Governing Law",
    "legal.version":       "Version",
    "legal.responseTime":  "Response Time",
    "legal.supportHours":  "Support Hours",
    "legal.priority":      "Priority",
    // Shared badge values
    "legal.lastUpdatedVal": "May 24, 2026",
    "legal.rwandaVal":      "Republic of Rwanda",
    "legal.gdprVal":        "GDPR-aligned",
    "legal.responseVal":    "Within 24 hours",
    "legal.hoursVal":       "Mon–Fri, 08:00–18:00",
    "legal.priorityVal":    "Critical issues: 2 hrs",

    // ── PRIVACY POLICY ──────────────────────────────────────────────────────
    "privacy.title":    "Privacy Policy",
    "privacy.subtitle": "Last updated: May 2026 · Effective immediately upon acceptance",

    "privacy.s1.title": "1. Who We Are",
    "privacy.s1.p1":    "Kunga Basics Ltd. ('we', 'us', 'our') operates the Kunga Basics Admin Portal and associated learning platform. We are registered in the Republic of Rwanda and are committed to protecting the personal data of all users, administrators, and subscribers who interact with our services.",
    "privacy.s1.p2":    "This Privacy Policy explains how we collect, use, store, and protect personal information when you use the Kunga Basics Admin Portal. By logging into this portal, you acknowledge that you have read and understood this policy.",

    "privacy.s2.title": "2. Data We Collect",
    "privacy.s2.lead":  "We collect the following categories of personal data:",
    "privacy.s2.i1":    "Account information: full name, email address, profile photo, and role designation.",
    "privacy.s2.i2":    "Authentication data: password hashes (never plain-text), Google OAuth identifiers.",
    "privacy.s2.i3":    "Activity logs: login timestamps, IP addresses, user-agent strings for security auditing.",
    "privacy.s2.i4":    "Preference settings: UI theme, language preference, MFA configuration.",
    "privacy.s2.i5":    "Content actions: modules created/edited, videos uploaded, announcements published.",

    "privacy.s3.title": "3. How We Use Your Data",
    "privacy.s3.lead":  "Your data is used exclusively for the following purposes:",
    "privacy.s3.i1":    "Authenticating your identity and maintaining session security.",
    "privacy.s3.i2":    "Personalising your admin portal experience (theme, language, notifications).",
    "privacy.s3.i3":    "Generating internal audit logs for compliance and security investigations.",
    "privacy.s3.i4":    "Sending system notifications, password reset emails, and platform alerts.",
    "privacy.s3.i5":    "Improving platform reliability through anonymised usage analytics.",
    "privacy.s3.note":  "We do not sell, rent, or share your personal data with third parties for marketing or advertising purposes.",

    "privacy.s4.title": "4. Data Storage & Retention",
    "privacy.s4.p1":    "All data is stored on Supabase-managed PostgreSQL infrastructure with end-to-end encryption in transit (TLS 1.3) and at rest (AES-256). Media assets (profile photos, course images) are stored on MinIO object storage with private bucket access controls.",
    "privacy.s4.p2":    "Activity logs are retained for 12 months. Account data is retained for the duration of employment/contract plus 90 days after account deactivation. You may request early deletion by contacting the platform administrator.",

    "privacy.s5.title": "5. Your Rights",
    "privacy.s5.lead":  "As a data subject, you have the right to:",
    "privacy.s5.i1":    "Access: request a copy of all personal data we hold about you.",
    "privacy.s5.i2":    "Rectification: correct inaccurate or incomplete information via Account Settings.",
    "privacy.s5.i3":    "Erasure: request deletion of your account and associated data.",
    "privacy.s5.i4":    "Portability: receive your data in a machine-readable format.",
    "privacy.s5.i5":    "Objection: object to specific processing activities.",
    "privacy.s5.i6":    "Restrict: request that we limit how we process your data.",

    "privacy.s6.title": "6. Security Measures",
    "privacy.s6.i1":    "Passwords are hashed using bcrypt with a work factor of 12.",
    "privacy.s6.i2":    "JWT tokens are short-lived (access: 15 min, refresh: 7 days).",
    "privacy.s6.i3":    "Multi-factor authentication (TOTP) is available for all admin accounts.",
    "privacy.s6.i4":    "All API communications are encrypted over HTTPS.",
    "privacy.s6.i5":    "Login attempts are logged with IP and user-agent for anomaly detection.",

    "privacy.s7.title":   "7. Contact",
    "privacy.s7.p1":      "For any privacy-related inquiries, data access requests, or complaints, please contact our Data Protection Officer at:",
    "privacy.s7.contact": "Kunga Basics Ltd. — Data Protection\n📧 privacy@kungabasics.com\n📍 Kigali, Republic of Rwanda",

    // ── TERMS OF SERVICE ────────────────────────────────────────────────────
    "terms.title":    "Terms of Service",
    "terms.subtitle": "Last updated: May 2026 · Please read carefully before using this portal",

    "terms.s1.title": "1. Acceptance of Terms",
    "terms.s1.p1":    "By accessing or using the Kunga Basics Admin Portal ('Portal'), you agree to be bound by these Terms of Service ('Terms'). If you do not agree to these Terms, you must not access or use the Portal.",
    "terms.s1.p2":    "These Terms apply to all administrators, content managers, and support staff who have been granted access to the Portal by Kunga Basics Ltd.",

    "terms.s2.title": "2. Access & Account Responsibilities",
    "terms.s2.lead":  "Your account grants you access to the Portal on behalf of Kunga Basics Ltd. You agree to:",
    "terms.s2.i1":    "Keep your login credentials strictly confidential and not share them with any third party.",
    "terms.s2.i2":    "Immediately notify the system administrator if you suspect unauthorised access.",
    "terms.s2.i3":    "Use the Portal only for legitimate administrative and operational purposes.",
    "terms.s2.i4":    "Maintain the accuracy of your profile information at all times.",
    "terms.s2.i5":    "Enable Multi-Factor Authentication (MFA) if required by your organisation's policy.",
    "terms.s2.i6":    "Log out of active sessions when using shared or public devices.",

    "terms.s3.title": "3. Permitted Use",
    "terms.s3.lead":  "You may use the Portal to:",
    "terms.s3.i1":    "Manage learning modules, videos, and educational content.",
    "terms.s3.i2":    "Administer user accounts and subscription plans.",
    "terms.s3.i3":    "Review analytics, financial reports, and operational metrics.",
    "terms.s3.i4":    "Publish announcements and manage platform communications.",
    "terms.s3.i5":    "Configure system settings within your assigned role permissions.",

    "terms.s4.title": "4. Prohibited Activities",
    "terms.s4.lead":  "You must not use the Portal to:",
    "terms.s4.i1":    "Attempt to gain access to accounts, systems, or data beyond your authorisation level.",
    "terms.s4.i2":    "Upload malicious code, viruses, or harmful content.",
    "terms.s4.i3":    "Scrape, harvest, or bulk-export user data without explicit authorisation.",
    "terms.s4.i4":    "Use the Portal to harass, intimidate, or harm any person.",
    "terms.s4.i5":    "Attempt to reverse-engineer, decompile, or circumvent any security feature.",
    "terms.s4.i6":    "Access the Portal using automated scripts, bots, or non-human agents.",
    "terms.s4.i7":    "Share, sell, or disclose confidential platform data to unauthorised parties.",

    "terms.s5.title": "5. Intellectual Property",
    "terms.s5.p1":    "All content, software, trademarks, and materials accessible through the Portal are the exclusive property of Kunga Basics Ltd. or its licensors. You are granted a limited, non-exclusive, non-transferable licence to use the Portal solely for its intended administrative purposes.",
    "terms.s5.p2":    "You may not copy, reproduce, distribute, or create derivative works from any Portal content without prior written permission from Kunga Basics Ltd.",

    "terms.s6.title": "6. Limitation of Liability",
    "terms.s6.p1":    "To the maximum extent permitted by applicable law, Kunga Basics Ltd. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Portal, including but not limited to data loss, business interruption, or reputational harm.",
    "terms.s6.p2":    "Kunga Basics Ltd. does not guarantee uninterrupted or error-free operation of the Portal and reserves the right to carry out maintenance or updates at any time.",

    "terms.s7.title": "7. Termination",
    "terms.s7.p1":    "Kunga Basics Ltd. reserves the right to suspend or terminate your access to the Portal immediately and without notice if you violate these Terms or engage in conduct deemed harmful to the platform, its users, or the organisation.",
    "terms.s7.p2":    "Upon termination, your right to access the Portal ceases immediately and any data associated with your account may be archived or deleted in accordance with our Privacy Policy.",

    "terms.s8.title": "8. Changes to Terms",
    "terms.s8.p1":    "We reserve the right to modify these Terms at any time. Material changes will be communicated via platform notification or email. Your continued use of the Portal after such changes constitutes your acceptance of the revised Terms.",

    "terms.s9.title":   "9. Governing Law",
    "terms.s9.p1":      "These Terms are governed by and construed in accordance with the laws of the Republic of Rwanda. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Rwanda.",
    "terms.s9.contact": "Kunga Basics Ltd. — Legal\n📧 legal@kungabasics.com\n📍 Kigali, Republic of Rwanda",

    // ── SUPPORT CENTER ──────────────────────────────────────────────────────
    "support.title":    "Support Center",
    "support.subtitle": "Get help, browse FAQs, or send us a message — we're here to help",
    "support.faqTitle": "Frequently Asked Questions",
    "support.formTitle":"Send Us a Message",

    "support.email.title":  "Email Support",
    "support.email.val":    "support@kungabasics.com",
    "support.email.sub":    "Best for non-urgent queries",
    "support.email.action": "Send email",
    "support.tech.title":   "Technical Issues",
    "support.tech.val":     "tech@kungabasics.com",
    "support.tech.sub":     "Bugs, outages, data issues",
    "support.tech.action":  "Send email",
    "support.docs.title":   "Documentation",
    "support.docs.val":     "Admin Portal Guide",
    "support.docs.sub":     "Full platform documentation",
    "support.docs.action":  "Open docs",

    "support.faq.q1": "How do I reset my password?",
    "support.faq.a1": "Click 'Forgot Password' on the sign-in page, enter your email address, and follow the reset link sent to your inbox. The link expires after 1 hour.",
    "support.faq.q2": "How do I enable Two-Factor Authentication (MFA)?",
    "support.faq.a2": "Go to Account Settings → Security → Two-Factor Authentication and toggle it on. An OTP will be required on every login once enabled.",
    "support.faq.q3": "How do I publish a learning module?",
    "support.faq.a3": "Navigate to Content → Modules, click '+ New Module', fill in the required details and content, then change its status to 'Published' using the status dropdown.",
    "support.faq.q4": "Why can't I see certain menu items?",
    "support.faq.a4": "Your admin role may have restricted access to some sections. Contact your system administrator to review your permissions. You can also restore hidden nav items in the sidebar by clicking 'Reset All'.",
    "support.faq.q5": "How are subscription payments processed?",
    "support.faq.a5": "Payments are processed via Mobile Money (MTN/Airtel) and card payments (Stripe). All transactions are recorded under Revenue → Mobile Money and Revenue → Card Payments respectively.",
    "support.faq.q6": "How do I manage the Ask Dr. Gad queue?",
    "support.faq.a6": "Go to Users → Ask Dr. Gad Queue. You can view, respond to, and mark questions as resolved. Questions are displayed in chronological order with the oldest unanswered ones highlighted.",
    "support.faq.q7": "What does the notification bell show?",
    "support.faq.a7": "The notification bell shows system alerts, new user registrations, subscription changes, flagged content, and other platform events. Unread notifications have a red badge counter.",
    "support.faq.q8": "How do I change the platform language?",
    "support.faq.a8": "Go to Account Settings → Preferences and select your preferred language. The portal supports English, Français, and Kinyarwanda. Your choice is saved to your account.",

    "support.form.subject":     "Subject",
    "support.form.subjectHint": "Brief description of your issue…",
    "support.form.message":     "Message",
    "support.form.messageHint": "Describe your issue in detail. Include steps to reproduce if reporting a bug…",
    "support.form.send":        "Send Message →",
    "support.sent.title":       "Message Sent!",
    "support.sent.body":        "We've received your message and will respond within 24 hours.",
  },

  // ── FRANÇAIS ───────────────────────────────────────────────────────────────
  fr: {
    "common.save":      "Enregistrer",
    "common.cancel":    "Annuler",
    "common.close":     "Fermer",
    "common.loading":   "Chargement…",
    "common.search":    "Rechercher…",
    "common.signOut":   "Déconnexion",
    "common.next":      "Suivant",
    "common.prev":      "Précédent",
    "common.saving":    "Enregistrement…",
    "common.updating":  "Mise à jour…",
    "common.sending":   "Envoi…",
    "common.confirm":   "Confirmer",
    "common.edit":      "Modifier",
    "common.delete":    "Supprimer",

    // ── Support Team ──────────────────────────────────────────────────
    "supportTeam.title":            "Équipe de support",
    "supportTeam.found":            "{n} membres",
    "supportTeam.searchPlaceholder": "Rechercher par nom ou e-mail...",
    "supportTeam.newMember":        "Nouveau membre",
    "supportTeam.create":           "Créer un membre",
    "supportTeam.editMember":       "Modifier le membre",
    "supportTeam.name":             "Nom",
    "supportTeam.email":            "E-mail",
    "supportTeam.phone":            "Téléphone",
    "supportTeam.roles":            "Rôles",
    "supportTeam.directPermissions": "Autorisations directes spécifiques",
    "supportTeam.active":           "Actif",
    "supportTeam.inactive":         "Inactif",
    "supportTeam.activate":         "Activer",
    "supportTeam.deactivate":       "Désactiver",
    "supportTeam.activated":        "Membre activé",
    "supportTeam.deactivated":      "Membre désactivé",
    "supportTeam.sendResetEmail":   "Envoyer la réinitialisation du mot de passe",
    "supportTeam.resetEmailSent":   "E-mail de réinitialisation envoyé",
    "supportTeam.manage":           "Gérer",
    "supportTeam.noneFound":        "Aucun membre de l'équipe de support trouvé",
    "supportTeam.nameEmailRequired": "Le nom et l'e-mail sont requis",
    "supportTeam.memberCreated":    "Membre créé — e-mail d'invitation envoyé",
    "supportTeam.memberUpdated":    "Membre mis à jour",
    "supportTeam.createFailed":     "Échec de la création du membre",
    "supportTeam.updateFailed":     "Échec de la mise à jour du membre",
    "supportTeam.credentialsTitle": "Membre créé",
    "supportTeam.credentialsNote":  "Partagez ce mot de passe temporaire de manière sécurisée avec le nouveau membre. Il ne sera plus affiché. Un e-mail de configuration a également été envoyé à sa boîte de réception.",
    "supportTeam.tempPassword":     "Mot de passe temporaire",
    "supportTeam.copy":             "Copier",
    "supportTeam.copied":           "Copié !",
    "supportTeam.done":             "Terminé",
    "supportTeam.regeneratePassword": "Régénérer le mot de passe",
    "supportTeam.confirmRegenerate": "Générer un nouveau mot de passe temporaire pour ce membre ? Son mot de passe actuel cessera de fonctionner immédiatement.",
    "supportTeam.passwordRegenerated": "Nouveau mot de passe temporaire généré",
    "supportTeam.regenerateFailed": "Échec de la régénération du mot de passe",

    // ── Roles & Permissions ───────────────────────────────────────────
    "roles.title":              "Rôles et autorisations",
    "roles.found":              "{n} rôles",
    "roles.newRole":            "Nouveau rôle",
    "roles.editRole":           "Modifier le rôle",
    "roles.name":               "Nom",
    "roles.description":        "Description",
    "roles.permissions":        "Autorisations",
    "roles.permissionCount":    "Autorisations",
    "roles.userCount":          "Utilisateurs",
    "roles.system":             "Système",
    "roles.all":                "Tous",
    "roles.noneFound":          "Aucun rôle trouvé",
    "roles.nameRequired":       "Le nom est requis",
    "roles.roleCreated":        "Rôle créé",
    "roles.roleUpdated":        "Rôle mis à jour",
    "roles.roleDeleted":        "Rôle supprimé",
    "roles.saveFailed":         "Échec de l'enregistrement du rôle",
    "roles.deleteFailed":       "Échec de la suppression du rôle",
    "roles.cannotDeleteSystem": "Les rôles système ne peuvent pas être supprimés",
    "roles.cannotDeleteAssigned": "Impossible de supprimer un rôle ayant des utilisateurs assignés",
    "roles.confirmDelete":      "Supprimer le rôle « {name} » ? Cette action est irréversible.",
    "roles.usersInRole":        "Utilisateurs avec le rôle « {name} »",
    "roles.noUsersAssigned":    "Aucun utilisateur assigné à ce rôle",
    "roles.tabRoles":           "Rôles",
    "roles.tabPermissions":     "Autorisations",
    "roles.searchPlaceholder":  "Rechercher par nom de rôle...",
    "roles.status":             "Statut",
    "roles.statusAll":          "Tous les statuts",
    "roles.statusActive":       "Actif",
    "roles.statusInactive":     "Inactif",
    "roles.createdFrom":        "Créé à partir de",
    "roles.noResultsFiltered":  "Aucun rôle ne correspond à vos filtres",

    // ── Permissions ────────────────────────────────────────────────────
    "permissions.title":            "Autorisations",
    "permissions.found":            "{n} autorisations",
    "permissions.searchPlaceholder": "Rechercher par nom ou code d'autorisation...",
    "permissions.category":         "Catégorie",
    "permissions.allCategories":    "Toutes les catégories",
    "permissions.code":             "Code",
    "permissions.description":      "Description",
    "permissions.noneFound":        "Aucune autorisation trouvée",

    // ── Audit Log ─────────────────────────────────────────────────────────────
    "auditLog.title":            "Journal d'audit",
    "auditLog.found":            "{n} entrées trouvées",
    "auditLog.searchPlaceholder":"Rechercher par action ou module…",
    "auditLog.allModules":       "Tous les modules",
    "auditLog.details":          "Détails",
    "auditLog.previousValue":    "Valeur précédente",
    "auditLog.newValue":         "Nouvelle valeur",
    "auditLog.system":           "Système",
    "auditLog.noneFound":        "Aucune entrée de journal d'audit trouvée",
    "auditLog.dateTime":         "Date et heure",
    "auditLog.module":           "Module",
    "auditLog.action":           "Action",
    "auditLog.entityType":       "Type d'entité",
    "auditLog.entityId":         "ID de l'entité",
    "auditLog.ipAddress":        "Adresse IP",
    "auditLog.browser":          "Navigateur",
    "auditLog.device":           "Appareil",
    "auditLog.sessionId":        "ID de session",
    "auditLog.manageColumns":    "Gérer les colonnes",
    "auditLog.notAvailable":     "Non disponible",

    "assessments.found":              "{n} évaluations trouvées",
    "assessments.searchPlaceholder":  "Rechercher par enfant, parent ou e-mail…",
    "assessments.noneFound":          "Aucune évaluation trouvée",
    "assessments.dateTime":           "Date et heure",
    "assessments.child":              "Enfant",
    "assessments.overall":            "Score global",
    "assessments.recommendedProgram": "Programme recommandé",
    "assessments.viewDetails":        "Voir les détails",
    "assessments.parent":             "Parent / Tuteur",
    "assessments.dob":                "Date de naissance",
    "assessments.gender":             "Genre",
    "assessments.country":            "Pays",
    "assessments.domainScores":       "Scores par domaine",
    "assessments.strengths":          "Points forts",
    "assessments.areasToSupport":     "Domaines à soutenir",
    "assessments.uploads":            "Fichiers téléchargés",
    "assessments.videos":             "vidéos",
    "assessments.photos":             "photos",
    "assessments.reports":            "rapports",

    "login.brand":      "Kunga Basics",
    "login.subtitle":   "Panneau Admin · v1.0",
    "login.email":      "Adresse e-mail",
    "login.password":   "Mot de passe",
    "login.signIn":     "Se connecter →",
    "login.signingIn":  "Connexion…",
    "login.adminOnly":  "Accès administrateur uniquement",

    "nav.overview":  "Aperçu",
    "nav.content":   "Contenu",
    "nav.users":     "Utilisateurs",
    "nav.revenue":   "Revenus",

    "nav.dashboard":     "Tableau de bord",
    "nav.analytics":     "Analytique",
    "nav.visitors":      "Analyse des visiteurs",
    "nav.modules":       "Modules",
    "nav.videos":        "Vidéos",
    "nav.announcements": "Annonces",
    "nav.allUsers":      "Tous les utilisateurs",
    "nav.subscriptions": "Abonnements",
    "nav.askGad":        "Demandez au Dr. Gad",
    "nav.assessments":   "Évaluations",
    "nav.donations":     "Dons",
    "nav.mobileMoney":   "Mobile Money",
    "nav.cardPayments":  "Paiements par carte",
    "nav.manualPayments": "Paiements manuels",
    "nav.transactions":  "Transactions de paiement",
    "nav.pricing":       "Tarification",
    "nav.administration":   "Administration",
    "nav.supportTeam":      "Équipe de support",
    "nav.rolesPermissions": "Rôles et permissions",
    "nav.auditLogs":        "Journal d'audit",
    "nav.countries":        "Disponibilité par Pays",

    "crumb.dashboard":    "Tableau de bord",
    "crumb.analytics":    "Analytique",
    "crumb.visitors":     "Analyse des visiteurs",
    "crumb.modules":      "Contenu / Modules",
    "crumb.moduleDetail": "Contenu / Détail du module",
    "crumb.videos":       "Contenu / Vidéos",
    "crumb.announcements":"Contenu / Annonces",
    "crumb.users":        "Utilisateurs / Tous",
    "crumb.userDetail":   "Utilisateurs / Profil",
    "crumb.subscriptions":"Utilisateurs / Abonnements",
    "crumb.askgad":       "Utilisateurs / File Dr. Gad",
    "crumb.donations":    "Revenus / Dons",
    "crumb.mobilemoney":  "Revenus / Mobile Money",
    "crumb.cardpay":      "Revenus / Paiements carte",
    "crumb.pricing":      "Revenus / Tarification",
    "crumb.account":      "Paramètres du compte",

    "search.dashboard":    "Rechercher le tableau de bord…",
    "search.analytics":    "Rechercher dans les analytiques…",
    "search.visitors":     "Rechercher dans l'analyse des visiteurs…",
    "search.modules":      "Rechercher des modules…",
    "search.videos":       "Rechercher des vidéos…",
    "search.announcements":"Rechercher des annonces…",
    "search.users":        "Rechercher des utilisateurs…",
    "search.subscriptions":"Rechercher des abonnements…",
    "search.askgad":       "Rechercher des soumissions…",
    "search.donations":    "Rechercher des dons…",
    "search.moduleDetail": "Rechercher vidéos et ressources…",
    "search.mobilemoney":  "Rechercher des transactions…",
    "search.cardpay":      "Rechercher paiements carte…",
    "search.pricing":      "Rechercher tarification…",
    "search.account":      "Rechercher paramètres…",

    "notif.title":       "Notifications",
    "notif.new":         "nouveau",
    "notif.allCaughtUp": "Tout est à jour !",
    "notif.noPending":   "Aucune action en attente pour le moment",
    "notif.tapToView":   "appuyer pour voir →",
    "notif.markAllRead": "Tout marquer comme lu",
    "notif.close":       "Fermer",

    "topbar.broadcast":      "Diffuser",
    "topbar.accountSettings":"Paramètres du compte",
    "topbar.manageSidebar":  "Gérer les onglets de la barre latérale",
    "sidebar.clickToHide":   "Cliquez sur 👁 pour masquer/afficher les onglets",
    "sidebar.resetAll":      "Réinitialiser — tout afficher",
    "sidebar.collapse":      "Réduire la barre latérale",
    "sidebar.expand":        "Développer la barre latérale",
    "sidebar.showTab":       "Afficher l'onglet",
    "sidebar.hideTab":       "Masquer l'onglet",

    "acct.profile":    "Profil",
    "acct.security":   "Sécurité",
    "acct.appearance": "Apparence",
    "acct.session":    "Session",
    "acct.language":   "Langue",

    "acct.profileInfo":          "Informations du profil",
    "acct.displayName":          "Nom d'affichage",
    "acct.emailAddress":         "Adresse e-mail",
    "acct.emailHint":            "L'adresse e-mail ne peut pas être modifiée ici.",
    "acct.adminRole":            "Administrateur · Accès complet",
    "acct.changePhoto":          "Changer la photo",
    "acct.uploading":            "Téléchargement {pct}%…",
    "acct.saveChanges":          "Enregistrer les modifications",
    "acct.saving":               "Enregistrement…",
    "acct.nameEmpty":            "Le nom ne peut pas être vide",
    "acct.nameSaved":            "✅ Nom mis à jour !",

    "acct.changePassword":       "Changer le mot de passe",
    "acct.pwHint":               "Choisissez un mot de passe fort d'au moins 8 caractères. Vous resterez connecté après le changement.",
    "acct.currentPw":            "Mot de passe actuel",
    "acct.currentPwPlaceholder": "Entrez votre mot de passe actuel",
    "acct.newPw":                "Nouveau mot de passe",
    "acct.newPwPlaceholder":     "Au moins 8 caractères",
    "acct.confirmPw":            "Confirmer le nouveau mot de passe",
    "acct.confirmPwPlaceholder": "Répéter le nouveau mot de passe",
    "acct.pwMismatch":           "Les mots de passe ne correspondent pas",
    "acct.updatePw":             "Mettre à jour le mot de passe",
    "acct.updating":             "Mise à jour…",
    "acct.allFieldsRequired":    "Tous les champs sont obligatoires",
    "acct.pwNoMatch":            "Les nouveaux mots de passe ne correspondent pas",
    "acct.pwTooShort":           "Le mot de passe doit contenir au moins 8 caractères",
    "acct.pwChanged":            "✅ Mot de passe modifié !",

    "acct.colorTheme":    "Thème de couleur",
    "acct.themeHint":     "Choisissez l'apparence du panneau admin. Le système suit les préférences de votre OS.",
    "acct.light":         "Clair",
    "acct.dark":          "Sombre",
    "acct.system":        "Système",
    "acct.preview":       "Aperçu",
    "acct.sampleCard":    "Carte exemple",
    "acct.sampleCardHint":"C'est ainsi qu'apparaîtront les cartes et le texte dans le thème sélectionné.",
    "acct.active":        "Actif",
    "acct.pending":       "En attente",

    "acct.sessionAccess":  "Session & Accès",
    "acct.role":           "Rôle",
    "acct.roleHint":       "Votre niveau d'accès dans le système",
    "acct.access":         "Accès",
    "acct.fullAdmin":      "Admin complet",
    "acct.accessHint":     "Accès lecture + écriture à toutes les ressources",
    "acct.authMethod":     "Méthode d'authentification",
    "acct.jwtBearer":      "JWT Bearer",
    "acct.authMethodHint": "Authentification par jeton",
    "acct.status":         "Statut",
    "acct.statusActive":   "Actif",
    "acct.statusHint":     "La session est actuellement valide",

    "acct.languageTitle":  "Langue / Language / Ururimi",
    "acct.languageHint":   "Choisissez la langue d'affichage de l'interface du panneau administrateur.",
    "acct.languageSaved":  "✅ Langue mise à jour !",
    "acct.langCurrent":    "Langue actuelle",

    "lang.en":  "English",
    "lang.fr":  "Français",
    "lang.kin": "Kinyarwanda",

    "signout.title":   "Se déconnecter ?",
    "signout.message": "Vous serez redirigé vers l'écran de connexion.",
    "signout.cancel":  "Annuler",
    "signout.confirm": "Déconnexion",

    "session.expired": "⚠️ Votre session a expiré. Veuillez vous reconnecter.",

    // ── En-têtes de colonnes (réutilisables) ────────────────────────────────
    "tbl.user":      "Utilisateur",
    "tbl.status":    "Statut",
    "tbl.plan":      "Plan",
    "tbl.platform":  "Plateforme",
    "tbl.expires":   "Expiration",
    "tbl.child":     "Enfant",
    "tbl.date":      "Date",
    "tbl.amount":    "Montant",
    "tbl.joined":    "Inscrit(e)",
    "tbl.action":    "Action",
    "tbl.method":    "Méthode",
    "tbl.campaign":  "Campagne",
    "tbl.donor":     "Donateur",
    "tbl.name":      "Nom",

    // ── Chaînes UI réutilisables ─────────────────────────────────────────────
    "ui.backToList":    "← Retour à la liste",
    "ui.clickForDetails": "cliquer sur la ligne pour les détails",
    "ui.never":         "Jamais",
    "ui.noData":        "Aucune donnée",
    "ui.showingOf":     "Affichage {n} sur {total}",
    "ui.anonymous":     "Anonyme",
    "ui.viewIn":        "Voir dans {page} →",
    "ui.goTo":          "Aller à {page} →",
    "ui.retry":         "↺ Réessayer",
    "ui.page":          "Page {n} sur {total}",
    "ui.clickToView":   "cliquer pour voir →",
    "ui.clickToFilter": "cliquer pour filtrer",
    "ui.active":        "● actif",

    // ── Tableau de bord ──────────────────────────────────────────────────────
    "dash.activeSubscribers":       "Abonnés actifs",
    "dash.monthlyRevenue":          "Revenu mensuel",
    "dash.gadQueue":                "File Dr. Gad",
    "dash.totalDonations":          "Total des dons",
    "dash.revenueHealth":           "Santé des revenus",
    "dash.fullBreakdown":           "Détail complet →",
    "dash.liveActivity":            "Activité en direct",
    "dash.noActivity":              "Aucune activité pour l'instant",
    "dash.recentlyActive":          "Utilisateurs récemment actifs",
    "dash.moduleCompletion":        "Taux de complétion des modules",
    "dash.weeklySignups":           "Inscriptions hebdomadaires & utilisateurs actifs",
    "dash.clickToView":             "cliquer pour voir →",
    "dash.clickBarForDetails":      "cliquer sur une barre pour les détails",
    "dash.noSignupData":            "Aucune donnée d'inscription encore",
    "dash.newSignups":              "Nouvelles inscriptions",
    "dash.activeUsers":             "Utilisateurs actifs",
    "dash.peakSignups":             "Pic d'inscriptions",
    "dash.peakActive":              "Pic actif",
    "dash.totalSignups":            "Total des inscriptions",
    "dash.liveFromDb":              "En direct de la base de données",
    "dash.mrr":                     "RMM",
    "dash.subscribers":             "Abonnés",
    "dash.churnRate":               "Taux de désabonnement",
    "dash.conversion":              "Conversion",
    "dash.trialUsers":              "Utilisateurs d'essai",
    "dash.scholarships":            "Bourses",
    "dash.subscriptionStatus":      "Statut d'abonnement",
    "dash.subscriptionExpires":     "Expiration de l'abonnement",
    "dash.viewInSubscriptions":     "Voir dans les abonnements →",
    "dash.allActiveSubscribers":    "Tous les abonnés actifs",
    "dash.goToSubscriptions":       "Aller aux abonnements →",
    "dash.monthlyRevenueBreakdown": "Détail des revenus mensuels",
    "dash.monthlyRecurring":        "Revenu mensuel récurrent",
    "dash.activeSubs":              "Abonnés actifs",
    "dash.hoverForStats":           "Survol pour stats · cliquer barre pour détails",
    "dash.byPlatform":              "Par plateforme",
    "dash.backToRevenue":           "← Retour aux revenus",
    "dash.share":                   "Part",
    "dash.gadQueueTitle":           "File Dr. Gad",
    "dash.backToQueue":             "← Retour à la file",
    "dash.submitted":               "Soumis",
    "dash.responseSent":            "✅ Réponse envoyée",
    "dash.responseVideo":           "Vidéo de réponse",
    "dash.writeResponse":           "Écrire une réponse",
    "dash.responseTextLabel":       "Texte de réponse *",
    "dash.responseVideoUrl":        "URL vidéo de réponse (optionnel)",
    "dash.sendResponse":            "✅ Envoyer la réponse",
    "dash.respondToQuestion":       "✏️ Répondre à cette question",
    "dash.openInAskGad":            "Ouvrir dans Demandez au Dr. Gad →",
    "dash.noSubmissions":           "Aucune soumission dans cette catégorie",
    "dash.goToAskGad":              "Aller à Demandez au Dr. Gad →",
    "dash.donationsOverview":       "Aperçu des dons",
    "dash.donationAmount":          "Montant du don",
    "dash.totalReceived":           "Total reçu",
    "dash.goToDonations":           "Aller aux dons →",
    "dash.viewInDonations":         "Voir dans les dons →",
    "dash.activityDetail":          "Détail de l'activité",
    "dash.userId":                  "ID utilisateur",
    "dash.adminId":                 "ID admin",
    "dash.completions":             "Complétions",
    "dash.completionRate":          "Taux de complétion",
    "dash.childAge":                "Âge de l'enfant",
    "dash.weekSignups":             "Inscriptions semaine {week}",
    "dash.period":                  "Période",
    "dash.signups":                 "Inscriptions",
    "dash.ipAddress":               "Adresse IP",
    "dash.osPlatform":              "OS / Plateforme",
    "dash.lastLogin":               "Dernière connexion",
    "dash.userDetails":             "Détails utilisateur",
    "dash.last6":                   "6 derniers connectés · cliquer pour détails",
    "dash.activeSubscribersModal":  "Abonnés actifs",
    "dash.totalActive":             "Total actifs",
    "dash.completionRateLabel":     "Taux de complétion",

    // ── Analytique ───────────────────────────────────────────────────────────
    "analytics.dau":               "UAJ",
    "analytics.mau":               "UAM",
    "analytics.stickiness":        "Engagement",
    "analytics.avgSession":        "Sess. moy.",
    "analytics.dailyActive":       "Utilisateurs actifs quotidiens",
    "analytics.monthlyActive":     "Utilisateurs actifs mensuels",
    "analytics.dauMauRatio":       "Ratio UAJ / UAM",
    "analytics.sessionLength":     "Durée de session",
    "analytics.subscriptionFunnel":"Entonnoir d'abonnement",
    "analytics.revenueByPlatform": "Revenus par plateforme",
    "analytics.platformPerformance":"Performance par plateforme",
    "analytics.mobileMoneyProviders":"Fournisseurs Mobile Money",
    "analytics.retentionSummary":  "Résumé de fidélisation",
    "analytics.retentionCohorts":  "Cohortes de fidélisation",
    "analytics.noRetentionData":   "Aucune donnée de fidélisation",
    "analytics.connectPostHog":    "Connectez PostHog pour suivre la fidélisation par cohorte. Une fois connecté, les cohortes hebdomadaires apparaîtront automatiquement.",
    "analytics.latestCohort":      "Dernière cohorte",
    "analytics.day1Retention":     "Fidélisation J1",
    "analytics.day7Retention":     "Fidélisation J7",
    "analytics.avg30dRetention":   "Fidélisation moy. 30j",
    "analytics.cohortSize":        "Taille de cohorte",
    "analytics.retentionCurve":    "Courbe de fidélisation",
    "analytics.allCohortsComparison":"Comparaison de toutes les cohortes",
    "analytics.funnel":            "Entonnoir : {step}",
    "analytics.fullFunnel":        "Entonnoir complet",
    "analytics.matchingUsers":     "Utilisateurs correspondants ({n})",
    "analytics.allPlatformsBreakdown":"Détail de toutes les plateformes",
    "analytics.pctShare":          "% de part",
    "analytics.cohort":            "Cohorte : {cohort}",
    "analytics.dropped":           "▼ {pct}% abandonnés",
    "analytics.liveFromDb":        "En direct",
    "analytics.activeSubscribers": "Abonnés actifs",
    "analytics.targetStickiness":  "🔥 Cible : 20%+",
    "analytics.postHog":           "Intégration PostHog",
    "analytics.clickForDetails":   "cliquer sur la ligne pour les détails",
    "analytics.clickSegment":      "cliquer sur le segment pour les détails",
    "analytics.activeBySource":    "abonnés actifs par source de paiement",
    "analytics.cohortsTracked":    "{n} cohortes suivies",
    "analytics.cohortsConnectHint":"Connectez PostHog pour suivre la fidélisation.",
    "analytics.usersInCohort":     "utilisateurs dans cette cohorte",
    "analytics.30dayRetention":    "Fidélisation 30 jours",
    "analytics.notEnoughData":     "données insuffisantes",
    "analytics.excellentRetention":"🔥 Excellente fidélisation",
    "analytics.needsImprovement":  "⚠️ À améliorer",
    "analytics.highChurnRisk":     "🔴 Risque de désabonnement élevé",
    "analytics.activeMobileMoneySubscribers": "{n} abonnés Mobile Money actifs",
    "analytics.pending":           "En attente",

    // ── Page utilisateurs ────────────────────────────────────────────────────
    "users.title":           "Gestion des utilisateurs",
    "users.found":           "{n} utilisateurs trouvés",
    "users.searchPlaceholder":"Rechercher par nom ou e-mail…",
    "users.allStatus":       "Tous les statuts",
    "users.active":          "🟢 Actif",
    "users.free":            "⚪ Gratuit / Aucun",
    "users.cancelled":       "🔴 Annulé",
    "users.expired":         "🟠 Expiré",
    "users.clear":           "Effacer",
    "users.sendEmail":       "Envoyer un e-mail",
    "users.view":            "Voir",
    "users.allUsers":        "Tous les utilisateurs",
    "users.failedToLoad":    "Échec du chargement de l'utilisateur",
    "users.userNotFound":    "Utilisateur introuvable",
    "users.modulesCompleted":"Modules terminés",
    "users.inProgress":      "En cours",
    "users.journalEntries":  "Entrées de journal",
    "users.gadQuestions":    "Questions Dr. Gad",
    "users.donations":       "Dons",
    "users.accountInfo":     "Infos du compte",
    "users.subscription":    "Abonnement",
    "users.childProfile":    "Profil de l'enfant",
    "users.appPreferences":  "Préférences de l'application",
    "users.moduleProgress":  "Progression des modules ({n} modules)",
    "users.recentActivity":  "Activité récente (20 derniers événements)",
    "users.gadSubmissions":  "Soumissions Dr. Gad ({n})",
    "users.donationHistory": "Historique des dons ({n})",
    "users.noActiveSubscription": "Aucun abonnement actif",
    "users.pushActive":      "Notifications push actives",
    "users.emailSubject":    "Sujet",
    "users.emailMessage":    "Message",
    "users.sendEmailBtn":    "Envoyer l'e-mail",
    "users.preparing":       "Préparation {fmt}…",
    "users.joined":          "Inscrit(e)",
    "users.lastLogin":       "Dernière connexion",
    "users.focusAreas":      "Domaines / difficultés",
    "users.done":            "Terminé",
    "users.inProgressStatus":"En cours",
    "users.noUsersFound":    "Aucun utilisateur trouvé",
    "users.noUsersMatching": "Aucun utilisateur correspondant à \"{q}\"",
    "users.pageOf":          "Page {page} sur {totalPages} · {total} total",
    "users.emailPlaceholder":"Sujet de l'e-mail…",
    "users.messagePlaceholder":"Écrivez votre message…",
    "users.sendEmailTitle":  "✉️ Envoyer un e-mail",
    "users.emailRequired":   "Sujet et message sont requis",
    "users.emailSent":       "✅ E-mail envoyé à {email}",
    "users.emailFailed":     "Échec : {err}",
    "users.role":            "Rôle",
    "users.pushToken":       "Token Push",
    "users.notConnected":    "Non connecté",
    "users.activeToken":     "Actif ✓",
    "users.renews":          "Renouvellement",
    "users.revenuecatId":    "ID RevenueCat",
    "users.childName":       "Prénom de l'enfant",
    "users.age":             "Âge",
    "users.ageMonths":       "{n} mois",
    "users.dateOfBirth":     "Date de naissance",
    "users.notifications":   "Notifications",
    "users.routineReminders":"Rappels quotidiens",
    "users.on":              "Actif",
    "users.off":             "Désactivé",
    "users.darkMode":        "Mode sombre",
    "users.textSize":        "Taille du texte",
    "users.langPref":        "Langue",
    "users.marketingEmails": "E-mails marketing",
    "users.optedIn":         "Inscrit(e)",
    "users.optedOut":        "Désinscrit(e)",

    // ── Modules ──────────────────────────────────────────────────────────────
    "mods.title":            "Gestion des modules",
    "mods.createModule":     "Créer un module",
    "mods.manageGroups":     "Gérer les groupes",
    "mods.published":        "Publié",
    "mods.draft":            "Brouillon",
    "mods.groups":           "Groupes",
    "mods.totalModules":     "Total des modules",
    "mods.allModules":       "tous les modules",
    "mods.liveForSubscribers":"en ligne pour les abonnés",
    "mods.unpublishedModules":"modules non publiés",
    "mods.contentCategories":"catégories de contenu",
    "mods.clickToManage":    "cliquer pour gérer",
    "mods.clickToReset":     "cliquer pour réinitialiser",
    "mods.noModulesMatch":   "Aucun module ne correspond à vos filtres",
    "mods.freePreview":      "Aperçu gratuit",
    "mods.completions":      "Achèvements",
    "mods.feedback":         "Retours",
    "mods.videos":           "Vidéos",
    "mods.sortOrder":        "Ordre de tri",
    "mods.preview":          "Aperçu",
    "mods.createdAt":        "Créé",
    "mods.nReviews":         "{n} avis",
    "mods.freePreviewCheck": "✅ Aperçu gratuit",
    "mods.subscribersOnly":  "🔒 Abonnés seulement",
    "mods.unpublish":        "⏸ Dépublier",
    "mods.publishModule":    "▶ Publier le module",
    "mods.publish":          "▶ Publier",
    "mods.manageResources":  "📎 Ressources",
    "mods.editResource":     "Modifier la ressource",
    "mods.addNewResource":   "Ajouter une ressource",
    "mods.storageMethod":    "Méthode de stockage",
    "mods.resourceType":     "Type de ressource",
    "mods.resourceTitle":    "Titre",
    "mods.resourceDesc":     "Description",
    "mods.fileSize":         "Taille du fichier",
    "mods.pageCount":        "Nombre de pages",
    "mods.visibility":       "Visibilité",
    "mods.addResource":      "Ajouter une ressource",
    "mods.noResourcesYet":   "Aucune ressource encore",
    "mods.uploading":        "Chargement…",
    "mods.createNewModule":  "📚 Créer un nouveau module",
    "mods.moduleCode":       "Code du module",
    "mods.groupLabel":       "Groupe",
    "mods.selectGroup":      "Choisir un groupe…",
    "mods.titleLabel":       "Titre",
    "mods.descLabel":        "Description",
    "mods.whatToExpect":     "À quoi s'attendre",
    "mods.freePreviewLabel": "Module en aperçu gratuit (accessible sans abonnement)",
    "mods.creating":         "Création…",
    "mods.editModule":       "✏️ Modifier le module",
    "mods.archived":         "Archivé",
    "mods.moduleGroups":     "📂 Groupes de modules",
    "mods.editGroup":        "Modifier le groupe",
    "mods.newGroup":         "Nouveau groupe",
    "mods.emoji":            "Emoji",
    "mods.groupName":        "Nom du groupe",
    "mods.createGroup":      "✅ Créer le groupe",
    "mods.groupCount":       "{n} groupes",
    "mods.noGroupsYet":      "Aucun groupe encore — créez-en un ci-dessus",
    "mods.done":             "Terminé",
    "mods.deleteGroup":      "🗑 Supprimer le groupe",
    "mods.deleteGroupConfirm":"Supprimer \"{name}\" ? Cela ne fonctionne que si le groupe n'a pas de modules. Cette action est irréversible.",
    "mods.deleting":         "Suppression…",
    "mods.moduleCount":      "{n} modules",
    "mods.code":             "Code",
    "mods.backToModules":    "← Modules",
    "mods.failedToLoad":     "Impossible de charger le module",
    "mods.completionsCTA":   "apprenants ont terminé",
    "mods.avgRating":        "Note moyenne",
    "mods.outOf50":          "sur 5.0",
    "mods.attachments":      "pièces jointes",
    "mods.videosResources":  "Vidéos & Ressources",
    "mods.editResources":    "✏️ Modifier les ressources",
    "mods.noVideosYet":      "Pas encore de vidéos",
    "mods.totalVideos":      "vidéos au total",

    // ── Vidéos ───────────────────────────────────────────────────────────────
    "videos.title":          "Gestion des vidéos",
    "videos.addVideo":       "Ajouter une vidéo",
    "videos.allVideos":      "Toutes les vidéos",
    "videos.noVideosMatch":  "Aucune vidéo ne correspond à vos filtres",
    "videos.createVideo":    "Créer une vidéo",
    "videos.editVideo":      "Modifier la vidéo",
    "videos.details":        "🎬 Détails de la vidéo",
    "videos.videoEnded":     "Vidéo terminée",
    "videos.replay":         "▶ Revoir",
    "videos.module":         "📦 Module",
    "videos.duration":       "⏱ Durée",
    "videos.added":          "📅 Ajouté",
    "videos.idLabel":        "🆔 ID",
    "videos.publish":        "📣 Publier",
    "videos.unpublish":      "📝 Dépublier",
    "videos.editBtn":        "✏️ Modifier",
    "videos.archive":        "🗑 Archiver",
    "videos.unknown":        "Inconnu",
    "videos.noVideosYet":    "Pas encore de vidéos",

    // ── Annonces ─────────────────────────────────────────────────────────────
    "anns.title":            "Créer une annonce",
    "anns.announcementTitle":"Titre",
    "anns.message":          "Message",
    "anns.targetAudience":   "Public cible",
    "anns.saveDraft":        "💾 Enregistrer le brouillon",
    "anns.publishNow":       "📣 Publier maintenant",
    "anns.confirmPublish":   "📣 Confirmer la publication",
    "anns.yesPublish":       "📣 Oui, publier maintenant",
    "anns.statistics":       "Statistiques des annonces",
    "anns.livePreview":      "📱 Aperçu en direct",
    "anns.totalSent":        "Total envoyé",
    "anns.pushDelivered":    "Push livré",
    "anns.bannerViews":      "Vues de la bannière",
    "anns.dismissals":       "Rejets",
    "anns.noAnnouncements":  "Aucune annonce encore",
    "anns.clickToViewWhoSaw":"Cliquer sur une ligne pour voir qui a vu cette annonce",
    "anns.details":          "📣 Détails de l'annonce",
    "anns.pushSent":         "Push envoyé",
    "anns.dismissed":        "Rejeté",
    "anns.openRate":         "Taux d'ouverture",
    "anns.created":          "Créé",
    "anns.publishedDate":    "Publié",
    "anns.draftOnly":        "Brouillon seulement",
    "anns.whoSawThis":       "Qui a vu ça",
    "anns.whoViewed":        "👁 Qui a regardé",
    "anns.noViewData":       "Aucune donnée de vue encore",
    "anns.viewedAt":         "Vu le",
    "anns.sentTitle":        "📣 Annonces envoyées",
    "anns.bannerViewsTitle": "👁 Vues de la bannière",
    "anns.pushDeliveredTitle":"📊 Push livré",
    "anns.dismissalsTitle":  "✅ Rejets",
    "anns.audience":         "Audience",
    "anns.dismissedBy":      "Rejeté par",
    "anns.sentDesc":         "Toutes les annonces publiées et leur portée de notification push.",
    "anns.bannerDesc":       "Annonces et nombre de fois que la bannière in-app a été vue.",
    "anns.pushDesc":         "Total des notifications push livrées par annonce.",
    "anns.dismissalsDesc":   "Utilisateurs qui ont appuyé ou rejeté la bannière in-app.",

    // ── Abonnements ──────────────────────────────────────────────────────────
    "subs.title":            "Abonnements ({n})",
    "subs.allPlatforms":     "Toutes les plateformes",
    "subs.allStatus":        "Tous les statuts",
    "subs.allPlans":         "Tous les plans",
    "subs.noMatch":          "Aucun abonnement ne correspond à vos filtres",
    "subs.restore":          "Restaurer",
    "subs.cancel":           "Annuler",
    "subs.filtering":        "● filtrage",
    "subs.clickToFilter":    "cliquer pour filtrer",
    "subs.details":          "💳 Détails de l'abonnement",
    "subs.periodStart":      "Début de période",
    "subs.periodEnd":        "Fin de période",
    "subs.cancelledAt":      "Annulé le",
    "subs.mobileProvider":   "Fournisseur mobile",
    "subs.mobilePhone":      "Téléphone mobile",
    "subs.restoreBtn":       "✅ Restaurer l'abonnement",
    "subs.cancelBtn":        "Annuler l'abonnement",

    // ── Demandez au Dr. Gad ──────────────────────────────────────────────────
    "askgad.pending":        "En attente",
    "askgad.underReview":    "En cours d'examen",
    "askgad.responded":      "Répondu",
    "askgad.total":          "Total",
    "askgad.allSubmissions": "toutes les soumissions",
    "askgad.awaitingResponse":"en attente de réponse",
    "askgad.beingReviewed":  "en cours d'examen",
    "askgad.completed":      "terminé",
    "askgad.noMatch":        "Aucune soumission ne correspond à vos filtres",
    "askgad.submissionDetails":"🎤 Détails de la soumission",
    "askgad.respondTitle":   "✍️ Répondre à la question",
    "askgad.writeResponse":  "Écrire une réponse",
    "askgad.yourResponse":   "Votre réponse",
    "askgad.pushNoteSent":   "(sera sauvegardée + notification push envoyée)",
    "askgad.sendResponse":   "Envoyer réponse + notification push",
    "askgad.drGadResponse":  "Réponse du Dr. Gad",
    "askgad.writeResponseBtn":"✍️ Écrire une réponse",
    "askgad.details":        "Détails",
    "askgad.respond":        "✍️ Répondre",

    // ── Dons ─────────────────────────────────────────────────────────────────
    "donations.title":           "Dons ({n})",
    "donations.totalReceived":   "Total reçu",
    "donations.scholarships":    "Bourses",
    "donations.pending":         "En attente",
    "donations.methods":         "Méthodes",
    "donations.noYet":           "Aucun don encore",
    "donations.details":         "💝 Détails du don",
    "donations.scholarshipDetails":"🎓 Détails de la bourse",
    "donations.donorWall":       "Mur des donateurs",
    "donations.visible":         "✅ Visible",
    "donations.private":         "🔒 Privé",
    "donations.scholarshipGranted":"Accès à la bourse accordé",
    "donations.grantedOn":       "Accordé le",
    "donations.notes":           "Notes",
    "donations.userId":          "ID utilisateur",
    "donations.activeLabel":     "Actif",
    "donations.email":           "E-mail",
    "donations.activeScholarships":"Bourses actives",
    "donations.donationsByMethod":"💳 Dons par méthode",
    "donations.confirmGrant":    "🎓 Confirmer l'attribution de bourse",
    "donations.grantByEmail":    "Attribuer par e-mail",
    "donations.grant":           "Attribuer",
    "donations.confirmGrantBtn": "✅ Confirmer l'attribution",
    "donations.totalReceivedLabel":"Total reçu",

    // ── Mobile Money ─────────────────────────────────────────────────────────
    "mm.title":              "Transactions ({n})",
    "mm.providerBreakdown":  "Détail par fournisseur",
    "mm.total":              "Total",
    "mm.active":             "Actif",
    "mm.pending":            "En attente",
    "mm.failed":             "Échoué",
    "mm.allProviders":       "Tous les fournisseurs",
    "mm.allStatus":          "Tous les statuts",
    "mm.allPlans":           "Tous les plans",
    "mm.noMatch":            "Aucune transaction ne correspond à vos filtres",
    "mm.transactionDetails": "📱 Détails de la transaction",
    "mm.activate":           "⚡ Activer",
    "mm.manualActivate":     "⚡ Activer manuellement l'abonnement",
    "mm.provider":           "Fournisseur",
    "mm.phone":              "Téléphone",
    "mm.txRef":              "Réf. transaction",
    "mm.userEmail":          "E-mail utilisateur",

    // ── En-têtes de tableaux ─────────────────────────────────────────────────
    "tbl.title":             "Titre",
    "tbl.type":              "Type",
    "tbl.source":            "Source",
    "tbl.duration":          "Durée",
    "tbl.added":             "Ajouté",
    "tbl.actions":           "Actions",
    "tbl.storage":           "Stockage",
    "tbl.description":       "Description",
    "tbl.code":              "Code",
    "tbl.videos":            "Vidéos",
    "tbl.preview":           "Aperçu",
    "tbl.completions":       "Achèvements",

    // ── Tarification ─────────────────────────────────────────────────────────
    "pricing.title":         "Tarification des abonnements",
    "pricing.subtitle":      "Gérez les prix, la durée d'essai et les paramètres d'intégration",
    "pricing.monthlyPlan":   "Plan mensuel",
    "pricing.annualPlan":    "Plan annuel",
    "pricing.freeTrial":     "Essai gratuit",
    "pricing.localCurrency": "Devise locale",
    "pricing.localCountry":  "Pays local",
    "pricing.refresh":       "Actualiser",
    "pricing.retry":         "Réessayer",
    "pricing.notSet":        "non défini",
    "pricing.editBtn":       "✏️ Modifier",
    "pricing.editTitle":     "✏️ Modifier {label}",
    "pricing.saveBtn":       "✅ Enregistrer",

    // ── Paiements par carte ──────────────────────────────────────────────────
    "cardpay.title":         "Paiements par carte — Stripe",
    "cardpay.subtitle":      "Abonnements Stripe et gestionnaires d'événements webhook",
    "cardpay.total":         "Total",
    "cardpay.active":        "Actif",
    "cardpay.trial":         "Essai",
    "cardpay.cancelled":     "Annulé",
    "cardpay.webhookHandlers":"Gestionnaires de webhook Stripe",
    "cardpay.allActive":     "Tous actifs",
    "cardpay.stripePaymentDetail":"Détail du paiement Stripe",
    "cardpay.stripeSubscriptions":"Abonnements Stripe ({n})",
    "cardpay.noStripeFound": "Aucun abonnement Stripe trouvé",
    "cardpay.allStripeSubs": "tous les abon. Stripe",
    "cardpay.payingSubscribers":"abonnés payants",
    "cardpay.inFreeTrial":   "en essai gratuit",
    "cardpay.churned":       "résiliés",
    "cardpay.stripeSubId":   "ID d'abonnement Stripe",
    "cardpay.stripeCustomer":"Client Stripe",

    // ── Libellés de statut ───────────────────────────────────────────────────
    "status.active":       "Actif",
    "status.published":    "Publié",
    "status.draft":        "Brouillon",
    "status.archived":     "Archivé",
    "status.trial":        "Essai",
    "status.cancelled":    "Annulé",
    "status.expired":      "Expiré",
    "status.pending":      "En attente",
    "status.scholarship":  "Bourse",
    "status.completed":    "Terminé",
    "status.failed":       "Échoué",
    "status.responded":    "Répondu",
    "status.none":         "Aucun",

    "filter.all":          "Tous",

    "subs.statusActive":      "✅ Actif",
    "subs.statusTrial":       "🟡 Essai",
    "subs.statusCancelled":   "❌ Annulé",
    "subs.statusExpired":     "🕐 Expiré",
    "subs.statusScholarship": "🎓 Bourse",
    "subs.planMonthly":       "Mensuel",
    "subs.planAnnual":        "Annuel",
    "subs.planScholarship":   "Bourse",
    "subs.childProfile":      "🧒 Profil de l'enfant",

    "askgad.filterReview":        "🔍 Examen",
    "askgad.filterDone":          "✅ Terminé",
    "askgad.statusSubmitted":     "⏳ En attente",
    "askgad.statusUnderReview":   "🔍 En cours d'examen",
    "askgad.statusResponded":     "✅ Répondu",
    "askgad.responsePlaceholder": "Écrivez votre réponse détaillée à ce parent…",

    "mods.allStatus":       "Tous les statuts",
    "mods.statusPublished": "✅ Publié",
    "mods.statusDraft":     "📝 Brouillon",
    "mods.statusArchived":  "🗃 Archivé",

    "videos.allTypes":          "Tous les types",
    "videos.typeIntro":         "Intro",
    "videos.typePractice":      "Pratique",
    "videos.typeContent":       "Contenu",
    "videos.typeExplanation":   "Explication",
    "videos.typeTaster":        "Avant-goût",
    "videos.typeDemonstration": "Démonstration",
    "videos.typeAssessment":    "Évaluation",
    "videos.selectModule":      "Choisir un module…",
    "videos.selectModuleDash":  "— Choisir un module —",
    "videos.previewClipLabel":  "Clip d'aperçu gratuit",
    "videos.ytFetching":        "Auto-remplissage depuis YouTube…",
    "videos.titlePlaceholder":  "ex. Introduction au développement de la parole",

    "anns.previewNow":           "maintenant",
    "anns.titleInputPh":         "Titre de l'annonce…",
    "anns.messageInputPh":       "Les parents verront ceci comme notification push et bannière in-app…",
    "anns.previewTitlePh":       "Titre de votre annonce",
    "anns.previewBodyPh":        "Votre message apparaîtra ici…",
    "anns.inAppBanner":          "Bannière In-App",
    "anns.bannerTitlePh":        "Titre",
    "anns.bannerBodyPh":         "Aperçu du message",
    "anns.target":               "Cible",
    "anns.viewersHeader":        "Spectateurs",
    "anns.targetAllSubscribers": "Tous les abonnés",
    "anns.targetActiveOnly":     "Actifs uniquement",
    "anns.targetSpeechModule":   "Utilisateurs du module discours",
    "anns.targetAllRegistered":  "Tous les inscrits",
    "anns.subAnnouncements":     "annonces créées",
    "anns.subPushNotifs":        "total notifications push",
    "anns.subBannerViews":       "vues de la bannière in-app",
    "anns.subConfirmedViews":    "vues de bannière confirmées",
    "anns.publishWarning":       "Cela enverra immédiatement une notification push à tous les utilisateurs correspondants. Cette action est irréversible.",
    "anns.confirmTarget":        "Cible : {target}",
    "anns.titleRequired":        "Titre et message requis",
    "anns.draftSaved":           "✅ Brouillon enregistré !",
    "anns.publishedToast":       "📣 Annonce publiée !",

    "donations.donationsCardTitle":"Dons ({n})",
    "donations.scholarshipsTitle": "Bourses ({n})",
    "donations.filterAll":         "Tous",
    "donations.filterCompleted":   "Terminé",
    "donations.filterPending":     "En attente",
    "donations.filterFailed":      "Échoué",
    "donations.noScholarshipsYet": "Aucune bourse encore",
    "donations.noData":            "Aucune donnée",
    "donations.activeBadge":       "actif",
    "donations.grantedLabel":      "Accordé le {date}",
    "donations.scholarshipsCount": "{n} bourses actives — accès complet accordé gratuitement.",
    "donations.donationsCount":    "{n} dons",

    "pricing.labelMonthlyUsd":   "Prix mensuel (USD)",
    "pricing.descMonthlyUsd":    "Prix de l'abonnement mensuel (USD)",
    "pricing.labelAnnualUsd":    "Prix annuel (USD)",
    "pricing.descAnnualUsd":     "Prix de l'abonnement annuel (USD)",
    "pricing.labelMonthlyLocal": "Prix mensuel (local)",
    "pricing.descMonthlyLocal":  "Prix mensuel en devise locale (étiquette optionnelle)",
    "pricing.labelAnnualLocal":  "Prix annuel (local)",
    "pricing.descAnnualLocal":   "Prix annuel en devise locale (étiquette optionnelle)",
    "pricing.labelCurrencyLocal":"Code devise locale",
    "pricing.descCurrencyLocal": "Code ISO de devise (ex. KES, NGN)",
    "pricing.labelLocalCountry": "Pays local",
    "pricing.descLocalCountry":  "Pays du marché principal",
    "pricing.labelTrialDays":    "Jours d'essai gratuit",
    "pricing.descTrialDays":     "Jours d'accès gratuit avant facturation",
    "pricing.labelStripeMonthly":"ID Prix Stripe Mensuel",
    "pricing.descStripeMonthly": "Price ID Stripe pour le plan mensuel",
    "pricing.labelStripeAnnual": "ID Prix Stripe Annuel",
    "pricing.descStripeAnnual":  "Price ID Stripe pour le plan annuel",
    "pricing.groupSubscription": "Tarification des abonnements",
    "pricing.groupTrial":        "Paramètres d'essai",
    "pricing.groupStripe":       "Intégration Stripe",
    "pricing.currency":          "Devise : {code}",
    "pricing.updated":           "Mis à jour le {date}",
    "pricing.priceHint":         "💡 Saisissez la valeur numérique uniquement (ex. 14.00 pour 14 $). Le symbole monétaire n'est pas nécessaire.",
    "pricing.thenCharged":       "Puis facturation mensuelle",
    "pricing.usdStripe":         "USD · Stripe / Flutterwave",

    "acct.mfa":             "Authentification à deux facteurs (2FA)",
    "acct.mfaHint":         "Ajoutez une couche de sécurité supplémentaire. Lorsqu'elle est activée, vous recevrez un code unique à chaque connexion.",
    "acct.mfaEnabled":      "La 2FA est activée sur votre compte",
    "acct.mfaDisabled":     "La 2FA est actuellement désactivée",
    "acct.enableMfa":       "Activer la 2FA",
    "acct.disableMfa":      "Désactiver la 2FA",
    "acct.sendOtp":         "Envoyer le code",
    "acct.sendingOtp":      "Envoi…",
    "acct.otpCode":         "Code de vérification",
    "acct.otpPlaceholder":  "Saisir le code à 6 chiffres",
    "acct.verifyOtp":       "Vérifier et activer",
    "acct.verifyingOtp":    "Vérification…",
    "acct.otpSent":         "✅ Code envoyé à {dest}",
    "acct.mfaEnabledToast": "✅ Authentification à deux facteurs activée !",
    "acct.mfaDisabledToast":"La 2FA a été désactivée",

    // ── PAGES LÉGALES ────────────────────────────────────────────────────────

    "crumb.privacy":  "Politique de confidentialité",
    "crumb.terms":    "Conditions d'utilisation",
    "crumb.support":  "Centre d'aide",
    "search.privacy": "Rechercher la politique…",
    "search.terms":   "Rechercher les conditions…",
    "search.support": "Rechercher l'aide…",

    "footer.privacy": "Confidentialité",
    "footer.terms":   "Conditions",
    "footer.support": "Aide",

    "legal.lastUpdated":   "Mise à jour",
    "legal.jurisdiction":  "Juridiction",
    "legal.dataStandard":  "Norme des données",
    "legal.effectiveDate": "Date d'entrée en vigueur",
    "legal.governingLaw":  "Droit applicable",
    "legal.version":       "Version",
    "legal.responseTime":  "Délai de réponse",
    "legal.supportHours":  "Heures de support",
    "legal.priority":      "Priorité",
    "legal.lastUpdatedVal": "24 mai 2026",
    "legal.rwandaVal":      "République du Rwanda",
    "legal.gdprVal":        "Conforme au RGPD",
    "legal.responseVal":    "Sous 24 heures",
    "legal.hoursVal":       "Lun–Ven, 08h00–18h00",
    "legal.priorityVal":    "Urgences : 2 h",

    // ── POLITIQUE DE CONFIDENTIALITÉ ────────────────────────────────────────
    "privacy.title":    "Politique de confidentialité",
    "privacy.subtitle": "Dernière mise à jour : mai 2026 · En vigueur dès acceptation",

    "privacy.s1.title": "1. Qui Sommes-Nous",
    "privacy.s1.p1":    "Kunga Basics Ltd. (« nous ») exploite le Portail d'administration Kunga Basics et la plateforme d'apprentissage associée. Nous sommes enregistrés en République du Rwanda et nous nous engageons à protéger les données personnelles de tous les utilisateurs, administrateurs et abonnés qui interagissent avec nos services.",
    "privacy.s1.p2":    "Cette Politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons les informations personnelles lorsque vous utilisez le Portail d'administration Kunga Basics. En vous connectant à ce portail, vous reconnaissez avoir lu et compris cette politique.",

    "privacy.s2.title": "2. Données que Nous Collectons",
    "privacy.s2.lead":  "Nous collectons les catégories suivantes de données personnelles :",
    "privacy.s2.i1":    "Informations de compte : nom complet, adresse e-mail, photo de profil et désignation de rôle.",
    "privacy.s2.i2":    "Données d'authentification : hachages de mots de passe (jamais en texte clair), identifiants Google OAuth.",
    "privacy.s2.i3":    "Journaux d'activité : horodatages de connexion, adresses IP, chaînes user-agent pour l'audit de sécurité.",
    "privacy.s2.i4":    "Paramètres de préférences : thème de l'interface, préférence de langue, configuration MFA.",
    "privacy.s2.i5":    "Actions sur le contenu : modules créés/modifiés, vidéos téléchargées, annonces publiées.",

    "privacy.s3.title": "3. Utilisation de Vos Données",
    "privacy.s3.lead":  "Vos données sont utilisées exclusivement aux fins suivantes :",
    "privacy.s3.i1":    "Authentifier votre identité et maintenir la sécurité de la session.",
    "privacy.s3.i2":    "Personnaliser votre expérience du portail administrateur (thème, langue, notifications).",
    "privacy.s3.i3":    "Générer des journaux d'audit internes pour la conformité et les enquêtes de sécurité.",
    "privacy.s3.i4":    "Envoyer des notifications système, des e-mails de réinitialisation de mot de passe et des alertes plateforme.",
    "privacy.s3.i5":    "Améliorer la fiabilité de la plateforme grâce à des analyses d'utilisation anonymisées.",
    "privacy.s3.note":  "Nous ne vendons, ne louons ni ne partageons vos données personnelles avec des tiers à des fins de marketing ou de publicité.",

    "privacy.s4.title": "4. Stockage et Conservation des Données",
    "privacy.s4.p1":    "Toutes les données sont stockées sur l'infrastructure PostgreSQL gérée par Supabase avec chiffrement de bout en bout en transit (TLS 1.3) et au repos (AES-256). Les fichiers médias (photos de profil, images de cours) sont stockés sur le stockage objet MinIO avec des contrôles d'accès aux compartiments privés.",
    "privacy.s4.p2":    "Les journaux d'activité sont conservés pendant 12 mois. Les données de compte sont conservées pendant la durée de l'emploi/contrat plus 90 jours après la désactivation du compte. Vous pouvez demander une suppression anticipée en contactant l'administrateur de la plateforme.",

    "privacy.s5.title": "5. Vos Droits",
    "privacy.s5.lead":  "En tant que personne concernée, vous avez le droit de :",
    "privacy.s5.i1":    "Accès : demander une copie de toutes les données personnelles que nous détenons vous concernant.",
    "privacy.s5.i2":    "Rectification : corriger les informations inexactes ou incomplètes via les Paramètres du compte.",
    "privacy.s5.i3":    "Effacement : demander la suppression de votre compte et des données associées.",
    "privacy.s5.i4":    "Portabilité : recevoir vos données dans un format lisible par machine.",
    "privacy.s5.i5":    "Opposition : vous opposer à des activités de traitement spécifiques.",
    "privacy.s5.i6":    "Limitation : demander que nous limitions la façon dont nous traitons vos données.",

    "privacy.s6.title": "6. Mesures de Sécurité",
    "privacy.s6.i1":    "Les mots de passe sont hachés avec bcrypt avec un facteur de travail de 12.",
    "privacy.s6.i2":    "Les tokens JWT ont une courte durée de vie (accès : 15 min, rafraîchissement : 7 jours).",
    "privacy.s6.i3":    "L'authentification multi-facteurs (TOTP) est disponible pour tous les comptes administrateurs.",
    "privacy.s6.i4":    "Toutes les communications API sont chiffrées via HTTPS.",
    "privacy.s6.i5":    "Les tentatives de connexion sont enregistrées avec IP et user-agent pour la détection d'anomalies.",

    "privacy.s7.title":   "7. Contact",
    "privacy.s7.p1":      "Pour toute demande relative à la confidentialité, demande d'accès aux données ou réclamation, veuillez contacter notre Délégué à la Protection des Données à :",
    "privacy.s7.contact": "Kunga Basics Ltd. — Protection des données\n📧 privacy@kungabasics.com\n📍 Kigali, République du Rwanda",

    // ── CONDITIONS D'UTILISATION ─────────────────────────────────────────────
    "terms.title":    "Conditions d'utilisation",
    "terms.subtitle": "Dernière mise à jour : mai 2026 · Veuillez lire attentivement avant d'utiliser ce portail",

    "terms.s1.title": "1. Acceptation des Conditions",
    "terms.s1.p1":    "En accédant ou en utilisant le Portail d'administration Kunga Basics (« Portail »), vous acceptez d'être lié par ces Conditions d'utilisation (« Conditions »). Si vous n'acceptez pas ces Conditions, vous ne devez pas accéder au Portail ni l'utiliser.",
    "terms.s1.p2":    "Ces Conditions s'appliquent à tous les administrateurs, gestionnaires de contenu et agents de support auxquels Kunga Basics Ltd. a accordé l'accès au Portail.",

    "terms.s2.title": "2. Accès et Responsabilités du Compte",
    "terms.s2.lead":  "Votre compte vous donne accès au Portail au nom de Kunga Basics Ltd. Vous vous engagez à :",
    "terms.s2.i1":    "Garder vos identifiants de connexion strictement confidentiels et ne pas les partager avec des tiers.",
    "terms.s2.i2":    "Notifier immédiatement l'administrateur système si vous suspectez un accès non autorisé.",
    "terms.s2.i3":    "Utiliser le Portail uniquement à des fins administratives et opérationnelles légitimes.",
    "terms.s2.i4":    "Maintenir l'exactitude de vos informations de profil en tout temps.",
    "terms.s2.i5":    "Activer l'authentification multi-facteurs (MFA) si requis par la politique de votre organisation.",
    "terms.s2.i6":    "Se déconnecter des sessions actives lors de l'utilisation d'appareils partagés ou publics.",

    "terms.s3.title": "3. Utilisation Autorisée",
    "terms.s3.lead":  "Vous pouvez utiliser le Portail pour :",
    "terms.s3.i1":    "Gérer les modules d'apprentissage, les vidéos et le contenu éducatif.",
    "terms.s3.i2":    "Administrer les comptes utilisateurs et les plans d'abonnement.",
    "terms.s3.i3":    "Consulter les analyses, les rapports financiers et les indicateurs opérationnels.",
    "terms.s3.i4":    "Publier des annonces et gérer les communications de la plateforme.",
    "terms.s3.i5":    "Configurer les paramètres système dans le cadre de vos permissions de rôle.",

    "terms.s4.title": "4. Activités Interdites",
    "terms.s4.lead":  "Vous ne devez pas utiliser le Portail pour :",
    "terms.s4.i1":    "Tenter d'accéder à des comptes, systèmes ou données au-delà de votre niveau d'autorisation.",
    "terms.s4.i2":    "Télécharger du code malveillant, des virus ou du contenu nuisible.",
    "terms.s4.i3":    "Extraire, collecter ou exporter en masse des données utilisateurs sans autorisation explicite.",
    "terms.s4.i4":    "Utiliser le Portail pour harceler, intimider ou nuire à toute personne.",
    "terms.s4.i5":    "Tenter de rétro-ingénierie, de décompilation ou de contournement de toute fonction de sécurité.",
    "terms.s4.i6":    "Accéder au Portail à l'aide de scripts automatisés, de robots ou d'agents non humains.",
    "terms.s4.i7":    "Partager, vendre ou divulguer des données confidentielles de la plateforme à des parties non autorisées.",

    "terms.s5.title": "5. Propriété Intellectuelle",
    "terms.s5.p1":    "Tout le contenu, les logiciels, les marques déposées et les matériaux accessibles via le Portail sont la propriété exclusive de Kunga Basics Ltd. ou de ses concédants. Vous bénéficiez d'une licence limitée, non exclusive et non transférable pour utiliser le Portail uniquement à des fins administratives.",
    "terms.s5.p2":    "Vous ne pouvez pas copier, reproduire, distribuer ou créer des œuvres dérivées à partir du contenu du Portail sans l'autorisation écrite préalable de Kunga Basics Ltd.",

    "terms.s6.title": "6. Limitation de Responsabilité",
    "terms.s6.p1":    "Dans toute la mesure permise par la loi applicable, Kunga Basics Ltd. ne saurait être tenue responsable de dommages indirects, accessoires, spéciaux, consécutifs ou punitifs résultant de votre utilisation ou de votre incapacité à utiliser le Portail, y compris la perte de données, l'interruption d'activité ou le préjudice de réputation.",
    "terms.s6.p2":    "Kunga Basics Ltd. ne garantit pas un fonctionnement ininterrompu ou sans erreur du Portail et se réserve le droit d'effectuer des opérations de maintenance ou des mises à jour à tout moment.",

    "terms.s7.title": "7. Résiliation",
    "terms.s7.p1":    "Kunga Basics Ltd. se réserve le droit de suspendre ou de résilier immédiatement et sans préavis votre accès au Portail si vous violez ces Conditions ou adoptez un comportement jugé préjudiciable à la plateforme, à ses utilisateurs ou à l'organisation.",
    "terms.s7.p2":    "À la résiliation, votre droit d'accéder au Portail cesse immédiatement et toute donnée associée à votre compte peut être archivée ou supprimée conformément à notre Politique de confidentialité.",

    "terms.s8.title": "8. Modifications des Conditions",
    "terms.s8.p1":    "Nous nous réservons le droit de modifier ces Conditions à tout moment. Les changements significatifs seront communiqués par notification de plateforme ou par e-mail. L'utilisation continue du Portail après de tels changements constitue votre acceptation des Conditions révisées.",

    "terms.s9.title":   "9. Droit Applicable",
    "terms.s9.p1":      "Ces Conditions sont régies et interprétées conformément aux lois de la République du Rwanda. Tout litige découlant de ces Conditions sera soumis à la juridiction exclusive des tribunaux du Rwanda.",
    "terms.s9.contact": "Kunga Basics Ltd. — Service juridique\n📧 legal@kungabasics.com\n📍 Kigali, République du Rwanda",

    // ── CENTRE D'AIDE ────────────────────────────────────────────────────────
    "support.title":    "Centre d'aide",
    "support.subtitle": "Obtenez de l'aide, parcourez les FAQ ou envoyez-nous un message — nous sommes là pour vous aider",
    "support.faqTitle": "Foire Aux Questions",
    "support.formTitle":"Envoyez-nous un message",

    "support.email.title":  "Support par e-mail",
    "support.email.val":    "support@kungabasics.com",
    "support.email.sub":    "Idéal pour les questions non urgentes",
    "support.email.action": "Envoyer un e-mail",
    "support.tech.title":   "Problèmes techniques",
    "support.tech.val":     "tech@kungabasics.com",
    "support.tech.sub":     "Bugs, pannes, problèmes de données",
    "support.tech.action":  "Envoyer un e-mail",
    "support.docs.title":   "Documentation",
    "support.docs.val":     "Guide du portail admin",
    "support.docs.sub":     "Documentation complète de la plateforme",
    "support.docs.action":  "Ouvrir la doc",

    "support.faq.q1": "Comment réinitialiser mon mot de passe ?",
    "support.faq.a1": "Cliquez sur « Mot de passe oublié » sur la page de connexion, saisissez votre adresse e-mail et suivez le lien de réinitialisation envoyé dans votre boîte de réception. Le lien expire après 1 heure.",
    "support.faq.q2": "Comment activer l'authentification à deux facteurs (MFA) ?",
    "support.faq.a2": "Allez dans Paramètres du compte → Sécurité → Authentification à deux facteurs et activez-la. Un code OTP sera requis à chaque connexion une fois activée.",
    "support.faq.q3": "Comment publier un module d'apprentissage ?",
    "support.faq.a3": "Allez dans Contenu → Modules, cliquez sur « + Nouveau module », remplissez les informations requises et le contenu, puis changez son statut en « Publié » via le menu déroulant.",
    "support.faq.q4": "Pourquoi ne puis-je pas voir certains éléments du menu ?",
    "support.faq.a4": "Votre rôle d'administrateur peut avoir restreint l'accès à certaines sections. Contactez votre administrateur système pour réviser vos permissions. Vous pouvez également restaurer les éléments masqués en cliquant sur « Réinitialiser tout » dans la barre latérale.",
    "support.faq.q5": "Comment les paiements d'abonnement sont-ils traités ?",
    "support.faq.a5": "Les paiements sont traités via Mobile Money (MTN/Airtel) et par carte (Stripe). Toutes les transactions sont enregistrées sous Revenus → Mobile Money et Revenus → Paiements par carte respectivement.",
    "support.faq.q6": "Comment gérer la file d'attente Demander au Dr. Gad ?",
    "support.faq.a6": "Allez dans Utilisateurs → File d'attente Dr. Gad. Vous pouvez consulter les questions, y répondre et les marquer comme résolues. Les questions sont affichées par ordre chronologique, les plus anciennes sans réponse étant mises en évidence.",
    "support.faq.q7": "Que montre la cloche de notifications ?",
    "support.faq.a7": "La cloche de notifications affiche les alertes système, les nouvelles inscriptions, les changements d'abonnement, les contenus signalés et d'autres événements de la plateforme. Les notifications non lues ont un compteur rouge.",
    "support.faq.q8": "Comment changer la langue de la plateforme ?",
    "support.faq.a8": "Allez dans Paramètres du compte → Préférences et sélectionnez votre langue préférée. Le portail prend en charge l'anglais, le français et le kinyarwanda. Votre choix est enregistré sur votre compte.",

    "support.form.subject":     "Objet",
    "support.form.subjectHint": "Brève description de votre problème…",
    "support.form.message":     "Message",
    "support.form.messageHint": "Décrivez votre problème en détail. Incluez les étapes pour le reproduire si vous signalez un bug…",
    "support.form.send":        "Envoyer →",
    "support.sent.title":       "Message envoyé !",
    "support.sent.body":        "Nous avons bien reçu votre message et vous répondrons dans les 24 heures.",
  },

  // ── KINYARWANDA ────────────────────────────────────────────────────────────
  kin: {
    "common.save":      "Bika impinduka",
    "common.cancel":    "Reka",
    "common.close":     "Funga",
    "common.loading":   "Gutegereza…",
    "common.search":    "Shakisha…",
    "common.signOut":   "Sohoka",
    "common.next":      "Ikurikira",
    "common.prev":      "Iyabanjirije",
    "common.saving":    "Bika…",
    "common.updating":  "Kuvugurura…",
    "common.sending":   "Kohereza…",
    "common.confirm":   "Emeza",
    "common.edit":      "Hindura",
    "common.delete":    "Siba",

    // ── Support Team ──────────────────────────────────────────────────
    "supportTeam.title":            "Itsinda Rishinzwe Ubufasha",
    "supportTeam.found":            "Abagize itsinda {n}",
    "supportTeam.searchPlaceholder": "Shakisha hakurikijwe izina cyangwa imeli...",
    "supportTeam.newMember":        "Umunyamuryango Mushya",
    "supportTeam.create":           "Kora Umunyamuryango",
    "supportTeam.editMember":       "Hindura Umunyamuryango",
    "supportTeam.name":             "Izina",
    "supportTeam.email":            "Imeli",
    "supportTeam.phone":            "Telefoni",
    "supportTeam.roles":            "Inshingano",
    "supportTeam.directPermissions": "Uburenganzira Bwihariye",
    "supportTeam.active":           "Birakora",
    "supportTeam.inactive":         "Ntibikora",
    "supportTeam.activate":         "Kwemeza",
    "supportTeam.deactivate":       "Guhagarika",
    "supportTeam.activated":        "Umunyamuryango yemejwe",
    "supportTeam.deactivated":      "Umunyamuryango yahagaritswe",
    "supportTeam.sendResetEmail":   "Ohereza Ihindurwa ry'Ijambobanga",
    "supportTeam.resetEmailSent":   "Imeli y'ihindurwa ry'ijambobanga yoherejwe",
    "supportTeam.manage":           "Gucunga",
    "supportTeam.noneFound":        "Nta munyamuryango w'itsinda ry'ubufasha wabonetse",
    "supportTeam.nameEmailRequired": "Izina n'imeli birakenewe",
    "supportTeam.memberCreated":    "Umunyamuryango yakozwe — imeli yo gutumira yoherejwe",
    "supportTeam.memberUpdated":    "Umunyamuryango yahinduwe",
    "supportTeam.createFailed":     "Kurema umunyamuryango byanze",
    "supportTeam.updateFailed":     "Guhindura umunyamuryango byanze",
    "supportTeam.credentialsTitle": "Umunyamuryango Yakozwe",
    "supportTeam.credentialsNote":  "Sangiza uyu munyamuryango mushya ijambobanga ry'agateganyo mu buryo bwizewe. Ntirizongera kugaragara. Imeli yo gushyiraho konti yoherejwe na none.",
    "supportTeam.tempPassword":     "Ijambobanga ry'Agateganyo",
    "supportTeam.copy":             "Koporora",
    "supportTeam.copied":           "Byakoporowe!",
    "supportTeam.done":             "Byarangiye",
    "supportTeam.regeneratePassword": "Ongera Ukore Ijambobanga",
    "supportTeam.confirmRegenerate": "Kora ijambobanga rishya ry'agateganyo kuri uyu munyamuryango? Ijambobanga rye rikoreshwa ubu rizahita ridakora.",
    "supportTeam.passwordRegenerated": "Ijambobanga rishya ry'agateganyo ryakozwe",
    "supportTeam.regenerateFailed": "Kongera gukora ijambobanga byanze",

    // ── Roles & Permissions ───────────────────────────────────────────
    "roles.title":              "Inshingano n'Uburenganzira",
    "roles.found":              "Inshingano {n}",
    "roles.newRole":            "Inshingano Nshya",
    "roles.editRole":           "Hindura Inshingano",
    "roles.name":               "Izina",
    "roles.description":        "Ibisobanuro",
    "roles.permissions":        "Uburenganzira",
    "roles.permissionCount":    "Uburenganzira",
    "roles.userCount":          "Abakoresha",
    "roles.system":             "Sisitemu",
    "roles.all":                "Byose",
    "roles.noneFound":          "Nta nshingano yabonetse",
    "roles.nameRequired":       "Izina rirakenewe",
    "roles.roleCreated":        "Inshingano yakozwe",
    "roles.roleUpdated":        "Inshingano yahinduwe",
    "roles.roleDeleted":        "Inshingano yasibwe",
    "roles.saveFailed":         "Kubika inshingano byanze",
    "roles.deleteFailed":       "Gusiba inshingano byanze",
    "roles.cannotDeleteSystem": "Inshingano za sisitemu ntizishobora gusibwa",
    "roles.cannotDeleteAssigned": "Ntushobora gusiba inshingano ifite abakoresha bayihawe",
    "roles.confirmDelete":      "Siba inshingano \"{name}\"? Ibi ntibishobora gusubizwa inyuma.",
    "roles.usersInRole":        "Abakoresha bafite inshingano \"{name}\"",
    "roles.noUsersAssigned":    "Nta mukoresha wahawe iyi nshingano",
    "roles.tabRoles":           "Inshingano",
    "roles.tabPermissions":     "Uburenganzira",
    "roles.searchPlaceholder":  "Shakisha hakurikijwe izina ry'inshingano...",
    "roles.status":             "Imimerere",
    "roles.statusAll":          "Imimerere Yose",
    "roles.statusActive":       "Birakora",
    "roles.statusInactive":     "Ntibikora",
    "roles.createdFrom":        "Byaremwe Guhera",
    "roles.noResultsFiltered":  "Nta nshingano ihuye n'ibyo washatse",

    // ── Permissions ────────────────────────────────────────────────────
    "permissions.title":            "Uburenganzira",
    "permissions.found":            "Uburenganzira {n}",
    "permissions.searchPlaceholder": "Shakisha hakurikijwe izina cyangwa kode y'uburenganzira...",
    "permissions.category":         "Icyiciro",
    "permissions.allCategories":    "Ibyiciro Byose",
    "permissions.code":             "Kode",
    "permissions.description":      "Ibisobanuro",
    "permissions.noneFound":        "Nta burenganzira bwabonetse",

    // ── Audit Log ─────────────────────────────────────────────────────────────
    "auditLog.title":            "Raporo y'Igenzura",
    "auditLog.found":            "Inyandiko {n} zabonetse",
    "auditLog.searchPlaceholder":"Shakisha hakurikijwe igikorwa cyangwa module…",
    "auditLog.allModules":       "Moduli Zose",
    "auditLog.details":          "Ibisobanuro",
    "auditLog.previousValue":    "Agaciro ka Mbere",
    "auditLog.newValue":         "Agaciro Gishya",
    "auditLog.system":           "Sisitemu",
    "auditLog.noneFound":        "Nta nyandiko y'igenzura yabonetse",
    "auditLog.dateTime":         "Itariki n'Igihe",
    "auditLog.module":           "Module",
    "auditLog.action":           "Igikorwa",
    "auditLog.entityType":       "Ubwoko bw'Icyo Bireba",
    "auditLog.entityId":         "ID y'Icyo Bireba",
    "auditLog.ipAddress":        "Aderesi ya IP",
    "auditLog.browser":          "Mushakisha",
    "auditLog.device":           "Igikoresho",
    "auditLog.sessionId":        "ID y'Igihe cyo Kwinjira",
    "auditLog.manageColumns":    "Gucunga Inkingi",
    "auditLog.notAvailable":     "Ntibiboneka",

    "assessments.found":              "{n} amasuzuma yabonetse",
    "assessments.searchPlaceholder":  "Shakisha ukurikije umwana, umubyeyi cyangwa imeyili…",
    "assessments.noneFound":          "Nta isuzuma ryabonetse",
    "assessments.dateTime":           "Itariki n'igihe",
    "assessments.child":              "Umwana",
    "assessments.overall":            "Amanota rusange",
    "assessments.recommendedProgram": "Porogaramu yasabwe",
    "assessments.viewDetails":        "Reba Birambuye",
    "assessments.parent":             "Umubyeyi / Umurezi",
    "assessments.dob":                "Itariki y'amavuko",
    "assessments.gender":             "Igitsina",
    "assessments.country":            "Igihugu",
    "assessments.domainScores":       "Amanota ku ngingo",
    "assessments.strengths":          "Ibyiza",
    "assessments.areasToSupport":     "Ahakenewe Inkunga",
    "assessments.uploads":            "Dosiye Zashyizweho",
    "assessments.videos":             "amavidewo",
    "assessments.photos":             "amafoto",
    "assessments.reports":            "raporo",

    "login.brand":      "Kunga Basics",
    "login.subtitle":   "Urubuga rw'Ubuyobozi · v1.0",
    "login.email":      "Imeli",
    "login.password":   "Ijambo ryibanga",
    "login.signIn":     "Injira →",
    "login.signingIn":  "Kwinjira…",
    "login.adminOnly":  "Injira y'ubuyobozi gusa",

    "nav.overview":  "Incamake",
    "nav.content":   "Ibikubiye",
    "nav.users":     "Abakoresha",
    "nav.revenue":   "Inyungu",

    "nav.dashboard":     "Ikibaho cy'ibikorwa",
    "nav.analytics":     "Isesengura",
    "nav.visitors":      "Isesengura ry'abasuye",
    "nav.modules":       "Ibice",
    "nav.videos":        "Amashusho",
    "nav.announcements": "Makamakuru",
    "nav.allUsers":      "Abakoresha bose",
    "nav.subscriptions": "Amasubscription",
    "nav.askGad":        "Baza Dr. Gad",
    "nav.assessments":   "Isuzuma",
    "nav.donations":     "Inkunga",
    "nav.mobileMoney":   "Amafaranga kuri telefone",
    "nav.cardPayments":  "Kwishyura na karita",
    "nav.manualPayments": "Kwishyura kw'intoki",
    "nav.transactions":  "Amafaranga yishyuwe",
    "nav.pricing":       "Igiciro",
    "nav.administration":   "Ubuyobozi",
    "nav.supportTeam":      "Itsinda ry'ubufasha",
    "nav.rolesPermissions": "Inshingano n'uburenganzira",
    "nav.auditLogs":        "Raporo y'igenzura",
    "nav.countries":        "Ubushobozi bw'Igihugu",

    "crumb.dashboard":    "Ikibaho cy'ibikorwa",
    "crumb.analytics":    "Isesengura",
    "crumb.visitors":     "Isesengura ry'abasuye",
    "crumb.modules":      "Ibikubiye / Ibice",
    "crumb.moduleDetail": "Ibikubiye / Ibisobanuro by'igice",
    "crumb.videos":       "Ibikubiye / Amashusho",
    "crumb.announcements":"Ibikubiye / Makamakuru",
    "crumb.users":        "Abakoresha / Bose",
    "crumb.userDetail":   "Abakoresha / Umwirondoro",
    "crumb.subscriptions":"Abakoresha / Amasubscription",
    "crumb.askgad":       "Abakoresha / Inzira ya Dr. Gad",
    "crumb.donations":    "Inyungu / Inkunga",
    "crumb.mobilemoney":  "Inyungu / Amafaranga kuri telefone",
    "crumb.cardpay":      "Inyungu / Kwishyura na karita",
    "crumb.pricing":      "Inyungu / Igiciro",
    "crumb.account":      "Igenamiterere ry'konti",

    "search.dashboard":    "Shakisha ikibaho…",
    "search.analytics":    "Shakisha isesengura…",
    "search.visitors":     "Shakisha isesengura ry'abasuye…",
    "search.modules":      "Shakisha ibice…",
    "search.videos":       "Shakisha amashusho…",
    "search.announcements":"Shakisha makamakuru…",
    "search.users":        "Shakisha abakoresha…",
    "search.subscriptions":"Shakisha amasubscription…",
    "search.askgad":       "Shakisha ibisubmissions…",
    "search.donations":    "Shakisha inkunga…",
    "search.moduleDetail": "Shakisha amashusho n'ibikoresho…",
    "search.mobilemoney":  "Shakisha imirimo…",
    "search.cardpay":      "Shakisha kwishyura na karita…",
    "search.pricing":      "Shakisha igiciro…",
    "search.account":      "Shakisha igenamiterere…",

    "notif.title":       "Amatangazo",
    "notif.new":         "mishya",
    "notif.allCaughtUp": "Byose byarakozwe!",
    "notif.noPending":   "Nta mirimo itegereje ubu",
    "notif.tapToView":   "kanda kureba →",
    "notif.markAllRead": "Shyira byose nk'ibyasomwe",
    "notif.close":       "Funga",

    "topbar.broadcast":      "Tangaza",
    "topbar.accountSettings":"Igenamiterere ry'konti",
    "topbar.manageSidebar":  "Gucunga inzira",
    "sidebar.clickToHide":   "Kanda 👁 guhisha/kwerekana tabs",
    "sidebar.resetAll":      "Subira aho hose",
    "sidebar.collapse":      "Gabanya inzira",
    "sidebar.expand":        "Nura inzira",
    "sidebar.showTab":       "Erekana tab",
    "sidebar.hideTab":       "Hisha tab",

    "acct.profile":    "Umwirondoro",
    "acct.security":   "Umutekano",
    "acct.appearance": "Isura",
    "acct.session":    "Iseance",
    "acct.language":   "Ururimi",

    "acct.profileInfo":          "Amakuru y'umwirondoro",
    "acct.displayName":          "Izina ryerekanwa",
    "acct.emailAddress":         "Aderesi ya imeli",
    "acct.emailHint":            "Imeli ntishobora guhindurwa hano.",
    "acct.adminRole":            "Umuyobozi · Uburenganzira bwose",
    "acct.changePhoto":          "Hindura ifoto",
    "acct.uploading":            "Kohereza {pct}%…",
    "acct.saveChanges":          "Bika impinduka",
    "acct.saving":               "Bika…",
    "acct.nameEmpty":            "Izina ntirishobora kuba ubusa",
    "acct.nameSaved":            "✅ Izina rivuguruwe!",

    "acct.changePassword":       "Hindura ijambo ryibanga",
    "acct.pwHint":               "Hitamo ijambo ryibanga ry'amagambo nibura 8. Uzakomeza kwinjira nyuma yo guhindura.",
    "acct.currentPw":            "Ijambo ryibanga rya none",
    "acct.currentPwPlaceholder": "Injiza ijambo ryibanga rya none",
    "acct.newPw":                "Ijambo ryibanga rishya",
    "acct.newPwPlaceholder":     "Nibura inyuguti 8",
    "acct.confirmPw":            "Emeza ijambo ryibanga rishya",
    "acct.confirmPwPlaceholder": "Subiramo ijambo ryibanga rishya",
    "acct.pwMismatch":           "Amagambo yibanga ntahuza",
    "acct.updatePw":             "Vugurura ijambo ryibanga",
    "acct.updating":             "Kuvugurura…",
    "acct.allFieldsRequired":    "Imyanya yose irakenewe",
    "acct.pwNoMatch":            "Amagambo mashya yibanga ntahuza",
    "acct.pwTooShort":           "Ijambo ryibanga rigomba kuba nibura inyuguti 8",
    "acct.pwChanged":            "✅ Ijambo ryibanga ryahindutse!",

    "acct.colorTheme":    "Isura y'amabara",
    "acct.themeHint":     "Hitamo uburyo ikibaho cy'ubuyobozi kiboneka. Sisitemu ikurikira amahitamo ya OS yawe.",
    "acct.light":         "Ukeye",
    "acct.dark":          "Icura",
    "acct.system":        "Sisitemu",
    "acct.preview":       "Reba mbere",
    "acct.sampleCard":    "Ikarita ingero",
    "acct.sampleCardHint":"Ni uburyo amakarita n'inyandiko bizaboneka mu isura yahitiwe.",
    "acct.active":        "Birakora",
    "acct.pending":       "Bitegereje",

    "acct.sessionAccess":  "Iseance & Uburenganzira",
    "acct.role":           "Uruhare",
    "acct.roleHint":       "Urwego rwawe rw'uburenganzira mu sisitemu",
    "acct.access":         "Uburenganzira",
    "acct.fullAdmin":      "Ubuyobozi bwose",
    "acct.accessHint":     "Uburenganzira bwo gusoma + kwandika ku bikoresho byose",
    "acct.authMethod":     "Uburyo bwo kwemeza",
    "acct.jwtBearer":      "JWT Bearer",
    "acct.authMethodHint": "Kwemeza hakoreshejwe token",
    "acct.status":         "Imimerere",
    "acct.statusActive":   "Birakora",
    "acct.statusHint":     "Iseance irakora neza ubu",

    "acct.languageTitle":  "Ururimi / Language / Langue",
    "acct.languageHint":   "Hitamo ururimi rw'urubuga rw'ubuyobozi.",
    "acct.languageSaved":  "✅ Ururimi rwavuguruwe!",
    "acct.langCurrent":    "Ururimi rw'ubu",

    "lang.en":  "English",
    "lang.fr":  "Français",
    "lang.kin": "Ikinyarwanda",

    "signout.title":   "Sohoka?",
    "signout.message": "Uzajyanwa ku ipaji yo kwinjira.",
    "signout.cancel":  "Reka",
    "signout.confirm": "Sohoka",

    "session.expired": "⚠️ Iseance yawe irarangiye. Nyamuneka winjire nanone.",

    // ── Inzitizi z'insonga (zikoreshwa kenshi) ───────────────────────────────
    "tbl.user":      "Umukoresha",
    "tbl.status":    "Imimerere",
    "tbl.plan":      "Gahunda",
    "tbl.platform":  "Urubuga",
    "tbl.expires":   "Irangira",
    "tbl.child":     "Umwana",
    "tbl.date":      "Itariki",
    "tbl.amount":    "Amafaranga",
    "tbl.joined":    "Yinjiye",
    "tbl.action":    "Igikorwa",
    "tbl.method":    "Uburyo",
    "tbl.campaign":  "Ibikorwa",
    "tbl.donor":     "Umugumanahame",
    "tbl.name":      "Izina",

    // ── Ibisobanuro bikoreshwa kenshi ────────────────────────────────────────
    "ui.backToList":    "← Subira ku rutonde",
    "ui.clickForDetails": "kanda umurongo kureba ibisobanuro",
    "ui.never":         "Ntabwo",
    "ui.noData":        "Nta makuru",
    "ui.showingOf":     "Erekana {n} muri {total}",
    "ui.anonymous":     "Ntazwi",
    "ui.viewIn":        "Reba muri {page} →",
    "ui.goTo":          "Jya kuri {page} →",
    "ui.retry":         "↺ Ongera ugerageze",
    "ui.page":          "Urupapuro {n} muri {total}",
    "ui.clickToView":   "kanda kureba →",
    "ui.clickToFilter": "kanda gutunganya",
    "ui.active":        "● birakora",

    // ── Ikibaho cy'ibikorwa ──────────────────────────────────────────────────
    "dash.activeSubscribers":       "Abonné birakora",
    "dash.monthlyRevenue":          "Inyungu za buri kwezi",
    "dash.gadQueue":                "Inzira ya Dr. Gad",
    "dash.totalDonations":          "Inkunga yose",
    "dash.revenueHealth":           "Ubuzima bw'inyungu",
    "dash.fullBreakdown":           "Ibisobanuro byose →",
    "dash.liveActivity":            "Ibikorwa by'ubu",
    "dash.noActivity":              "Nta bikorwa kugeza ubu",
    "dash.recentlyActive":          "Abakoresha baherukira gukoreshwa",
    "dash.moduleCompletion":        "Ijanisha ry'ibice byarangiye",
    "dash.weeklySignups":           "Abiyandikishe bya buri cyumweru & abakoresha birakora",
    "dash.clickToView":             "kanda kureba →",
    "dash.clickBarForDetails":      "kanda umupira kureba ibisobanuro",
    "dash.noSignupData":            "Nta makuru y'iyandikisha kugeza ubu",
    "dash.newSignups":              "Iyandikisha rishya",
    "dash.activeUsers":             "Abakoresha birakora",
    "dash.peakSignups":             "Umurego w'iyandikisha",
    "dash.peakActive":              "Umurego w'ibikorwa",
    "dash.totalSignups":            "Iyandikisha ryose",
    "dash.liveFromDb":              "Bituruka mu bubiko",
    "dash.mrr":                     "RMK",
    "dash.subscribers":             "Abonné",
    "dash.churnRate":               "Ijanisha ry'ababohoka",
    "dash.conversion":              "Guhindurwa",
    "dash.trialUsers":              "Abakoresha b'ikizamini",
    "dash.scholarships":            "Buruse",
    "dash.subscriptionStatus":      "Imimerere y'ubusubire",
    "dash.subscriptionExpires":     "Ubusubire burarangira",
    "dash.viewInSubscriptions":     "Reba mu bisubire →",
    "dash.allActiveSubscribers":    "Abonné bose birakora",
    "dash.goToSubscriptions":       "Jya ku bisubire →",
    "dash.monthlyRevenueBreakdown": "Ibisobanuro by'inyungu z'ukwezi",
    "dash.monthlyRecurring":        "Inyungu z'ukwezi zigaruka",
    "dash.activeSubs":              "Bisubire birakora",
    "dash.hoverForStats":           "Survo ibiharuro · kanda umupira kureba ibisobanuro",
    "dash.byPlatform":              "Hakurikijwe urubuga",
    "dash.backToRevenue":           "← Subira ku nyungu",
    "dash.share":                   "Igice",
    "dash.gadQueueTitle":           "Inzira ya Dr. Gad",
    "dash.backToQueue":             "← Subira ku nzira",
    "dash.submitted":               "Byoherejwe",
    "dash.responseSent":            "✅ Igisubizo cyoherejwe",
    "dash.responseVideo":           "Videyo y'igisubizo",
    "dash.writeResponse":           "Andika igisubizo",
    "dash.responseTextLabel":       "Inyandiko y'igisubizo *",
    "dash.responseVideoUrl":        "URL ya videyo y'igisubizo (ntabwo ngombwa)",
    "dash.sendResponse":            "✅ Ohereza igisubizo",
    "dash.respondToQuestion":       "✏️ Subiza iki kibazo",
    "dash.openInAskGad":            "Fungura muri Baza Dr. Gad →",
    "dash.noSubmissions":           "Nta byo byoherejwe muri iyi kategori",
    "dash.goToAskGad":              "Jya kuri Baza Dr. Gad →",
    "dash.donationsOverview":       "Incamake y'inkunga",
    "dash.donationAmount":          "Ingano y'inkunga",
    "dash.totalReceived":           "Byose byakiriwe",
    "dash.goToDonations":           "Jya ku nkunga →",
    "dash.viewInDonations":         "Reba mu nkunga →",
    "dash.activityDetail":          "Ibisobanuro by'igikorwa",
    "dash.userId":                  "ID y'umukoresha",
    "dash.adminId":                 "ID ya nyirubwite",
    "dash.completions":             "Ibyarangiye",
    "dash.completionRate":          "Ijanisha ryo kurangiza",
    "dash.childAge":                "Imyaka y'umwana",
    "dash.weekSignups":             "Iyandikisha rya cyumweru {week}",
    "dash.period":                  "Igihe",
    "dash.signups":                 "Iyandikisha",
    "dash.ipAddress":               "Aderesi IP",
    "dash.osPlatform":              "OS / Urubuga",
    "dash.lastLogin":               "Kwinjira kwa nyuma",
    "dash.userDetails":             "Ibisobanuro by'umukoresha",
    "dash.last6":                   "6 baheruka kwinjira · kanda kureba ibisobanuro",
    "dash.activeSubscribersModal":  "Abonné birakora",
    "dash.totalActive":             "Byose birakora",
    "dash.completionRateLabel":     "Ijanisha ryo kurangiza",

    // ── Isesengura ───────────────────────────────────────────────────────────
    "analytics.dau":               "AKU",
    "analytics.mau":               "AKM",
    "analytics.stickiness":        "Gukomera",
    "analytics.avgSession":        "Iseance y'hagati",
    "analytics.dailyActive":       "Abakoresha birakora buri munsi",
    "analytics.monthlyActive":     "Abakoresha birakora buri kwezi",
    "analytics.dauMauRatio":       "Ijanisha AKU / AKM",
    "analytics.sessionLength":     "Uburebure bw'iseance",
    "analytics.subscriptionFunnel":"Inzira y'ubusubire",
    "analytics.revenueByPlatform": "Inyungu hakurikijwe urubuga",
    "analytics.platformPerformance":"Imikorere y'urubuga",
    "analytics.mobileMoneyProviders":"Abatanga Mobile Money",
    "analytics.retentionSummary":  "Incamake y'ukukumira",
    "analytics.retentionCohorts":  "Amatsinda y'ukukumira",
    "analytics.noRetentionData":   "Nta makuru y'ukukumira kugeza ubu",
    "analytics.connectPostHog":    "Unganisha PostHog gukurikirana ukukumira kw'itsinda. Amatsinda y'icyumweru azagaragara hano nyuma yo guhuza.",
    "analytics.latestCohort":      "Itsinda rya nyuma",
    "analytics.day1Retention":     "Ukukumira Umunsi 1",
    "analytics.day7Retention":     "Ukukumira Umunsi 7",
    "analytics.avg30dRetention":   "Ukukumira kw'hagati kw'iminsi 30",
    "analytics.cohortSize":        "Ingano y'itsinda",
    "analytics.retentionCurve":    "Umurongo w'ukukumira",
    "analytics.allCohortsComparison":"Gusesengura amatsinda yose",
    "analytics.funnel":            "Inzira: {step}",
    "analytics.fullFunnel":        "Inzira yose",
    "analytics.matchingUsers":     "Abakoresha bahuye ({n})",
    "analytics.allPlatformsBreakdown":"Ibisobanuro by'imbuga zose",
    "analytics.pctShare":          "% igice",
    "analytics.cohort":            "Itsinda: {cohort}",
    "analytics.dropped":           "▼ {pct}% basezerewe",
    "analytics.liveFromDb":        "Bituruka live",
    "analytics.activeSubscribers": "Abonné birakora",
    "analytics.targetStickiness":  "🔥 Intego: 20%+",
    "analytics.postHog":           "Guhuza na PostHog",
    "analytics.clickForDetails":   "kanda umurongo kureba ibisobanuro",
    "analytics.clickSegment":      "kanda igice kureba ibisobanuro",
    "analytics.activeBySource":    "abonné birakora hakurikijwe inkomoko y'ubwishyu",
    "analytics.cohortsTracked":    "Amatsinda {n} akurikiranwa",
    "analytics.cohortsConnectHint":"Unganisha PostHog gukurikirana ukukumira.",
    "analytics.usersInCohort":     "abakoresha muri iri tsinda",
    "analytics.30dayRetention":    "Ukukumira kw'iminsi 30",
    "analytics.notEnoughData":     "amakuru ntanshi",
    "analytics.excellentRetention":"🔥 Ukukumira gukomeye",
    "analytics.needsImprovement":  "⚠️ Bigomba kunozwa",
    "analytics.highChurnRisk":     "🔴 Ingorane nkuru zo guva",
    "analytics.activeMobileMoneySubscribers": "Abonné {n} ba Mobile Money birakora",
    "analytics.pending":           "Bitegereje",

    // ── Urupapuro rw'abakoresha ──────────────────────────────────────────────
    "users.title":           "Gucunga abakoresha",
    "users.found":           "Abakoresha {n} babonetse",
    "users.searchPlaceholder":"Shakisha izina cyangwa imeli…",
    "users.allStatus":       "Imimerere yose",
    "users.active":          "🟢 Birakora",
    "users.free":            "⚪ Ubuntu / Nta na kimwe",
    "users.cancelled":       "🔴 Byahagaritswe",
    "users.expired":         "🟠 Byarangiye",
    "users.clear":           "Siba",
    "users.sendEmail":       "Ohereza imeli",
    "users.view":            "Reba",
    "users.allUsers":        "Abakoresha bose",
    "users.failedToLoad":    "Kuzana umukoresha ntibyakunze",
    "users.userNotFound":    "Umukoresha ntaboneka",
    "users.modulesCompleted":"Ibice byarangiye",
    "users.inProgress":      "Biragenda",
    "users.journalEntries":  "Inyandiko za journal",
    "users.gadQuestions":    "Ibibazo bya Dr. Gad",
    "users.donations":       "Inkunga",
    "users.accountInfo":     "Amakuru y'konti",
    "users.subscription":    "Ubusubire",
    "users.childProfile":    "Umwirondoro w'umwana",
    "users.appPreferences":  "Amahitamo ya porogaramu",
    "users.moduleProgress":  "Iterambere ry'ibice ({n} ibice)",
    "users.recentActivity":  "Ibikorwa bya vuba (ibyakozwe 20 biheruka)",
    "users.gadSubmissions":  "Ibyo byoherejwe kuri Dr. Gad ({n})",
    "users.donationHistory": "Amateka y'inkunga ({n})",
    "users.noActiveSubscription": "Nta busubire burakora",
    "users.pushActive":      "Amatangazo ya push birakora",
    "users.emailSubject":    "Inyito",
    "users.emailMessage":    "Ubutumwa",
    "users.sendEmailBtn":    "Ohereza imeli",
    "users.preparing":       "Gutegura {fmt}…",
    "users.joined":          "Yinjiye",
    "users.lastLogin":       "Kwinjira kwa nyuma",
    "users.focusAreas":      "Inzego / ingorane",
    "users.done":            "Byarangiye",
    "users.inProgressStatus":"Biragenda",
    "users.noUsersFound":    "Nta bakoresha babonetse",
    "users.noUsersMatching": "Nta bakoresha bahuye na \"{q}\"",
    "users.pageOf":          "Urupapuro {page} muri {totalPages} · {total} byose",
    "users.emailPlaceholder":"Inyito y'imeli…",
    "users.messagePlaceholder":"Andika ubutumwa bwawe…",
    "users.sendEmailTitle":  "✉️ Ohereza imeli",
    "users.emailRequired":   "Inyito n'ubutumwa bikenewe",
    "users.emailSent":       "✅ Imeli yoherejwe kuri {email}",
    "users.emailFailed":     "Ntibashije: {err}",
    "users.role":            "Inshingano",
    "users.pushToken":       "Token ya push",
    "users.notConnected":    "Ntiyunganye",
    "users.activeToken":     "Birakora ✓",
    "users.renews":          "Uzongererwa",
    "users.revenuecatId":    "ID ya RevenueCat",
    "users.childName":       "Izina ry'umwana",
    "users.age":             "Imyaka",
    "users.ageMonths":       "Amezi {n}",
    "users.dateOfBirth":     "Itariki y'amavuko",
    "users.notifications":   "Amatangazo",
    "users.routineReminders":"Ibizira bya buri munsi",
    "users.on":              "Birakora",
    "users.off":             "Byahagaritswe",
    "users.darkMode":        "Imiterere y'icyizezi",
    "users.textSize":        "Ingano y'inyandiko",
    "users.langPref":        "Ururimi",
    "users.marketingEmails": "Imeli zo gutangaza",
    "users.optedIn":         "Byemewe",
    "users.optedOut":        "Byanzwe",

    // ── Ibice ────────────────────────────────────────────────────────────────
    "mods.title":            "Gucunga ibice",
    "mods.createModule":     "Shiraho igice",
    "mods.manageGroups":     "Gucunga amatsinda",
    "mods.published":        "Byatangarijwe",
    "mods.draft":            "Igenamigambi",
    "mods.groups":           "Amatsinda",
    "mods.totalModules":     "Ibice byose",
    "mods.allModules":       "ibice byose",
    "mods.liveForSubscribers":"birakora ku bonée",
    "mods.unpublishedModules":"ibice bitangarijwe",
    "mods.contentCategories":"indinganizo z'ibikubiye",
    "mods.clickToManage":    "kanda gucunga",
    "mods.clickToReset":     "kanda gusubira aho",
    "mods.noModulesMatch":   "Nta bice bihuye n'inyuzuzanyo zawe",
    "mods.freePreview":      "Kureba ubuntu",
    "mods.completions":      "Ibyarangiye",
    "mods.feedback":         "Ibitekerezo",
    "mods.videos":           "Amavidewo",
    "mods.sortOrder":        "Urutonde",
    "mods.preview":          "Kureba",
    "mods.createdAt":        "Byakozwe",
    "mods.nReviews":         "Ibitekerezo {n}",
    "mods.freePreviewCheck": "✅ Kureba ubuntu",
    "mods.subscribersOnly":  "🔒 Abasubscriber gusa",
    "mods.unpublish":        "⏸ Hagarika gukwirakwiza",
    "mods.publishModule":    "▶ Tangaza module",
    "mods.publish":          "▶ Tangaza",
    "mods.manageResources":  "📎 Ibyunguriro",
    "mods.editResource":     "Hindura ibyunguriro",
    "mods.addNewResource":   "Ongeramo ibyunguriro bishya",
    "mods.storageMethod":    "Uburyo bwo kubika",
    "mods.resourceType":     "Ubwoko bw'ibyunguriro",
    "mods.resourceTitle":    "Umutwe",
    "mods.resourceDesc":     "Ibisobanuro",
    "mods.fileSize":         "Ingano ya dosiye",
    "mods.pageCount":        "Umubare w'urupapuro",
    "mods.visibility":       "Kuboneka",
    "mods.addResource":      "Ongeramo ibyunguriro",
    "mods.noResourcesYet":   "Nta byunguriro",
    "mods.uploading":        "Kohereza…",
    "mods.createNewModule":  "📚 Kora module nshya",
    "mods.moduleCode":       "Code ya module",
    "mods.groupLabel":       "Itsinda",
    "mods.selectGroup":      "Hitamo itsinda…",
    "mods.titleLabel":       "Umutwe",
    "mods.descLabel":        "Ibisobanuro",
    "mods.whatToExpect":     "Ibizabaho",
    "mods.freePreviewLabel": "Module yo kureba ubuntu (igereka nta subscription)",
    "mods.creating":         "Kurema…",
    "mods.editModule":       "✏️ Hindura module",
    "mods.archived":         "Binyambare",
    "mods.moduleGroups":     "📂 Amatsinda ya module",
    "mods.editGroup":        "Hindura itsinda",
    "mods.newGroup":         "Itsinda rishya",
    "mods.emoji":            "Emoji",
    "mods.groupName":        "Izina ry'itsinda",
    "mods.createGroup":      "✅ Kora itsinda",
    "mods.groupCount":       "Amatsinda {n}",
    "mods.noGroupsYet":      "Nta matsinda — kora rimwe haruguru",
    "mods.done":             "Byarangiye",
    "mods.deleteGroup":      "🗑 Siba itsinda",
    "mods.deleteGroupConfirm":"Siba \"{name}\"? Ibi bikora gusa nta module ifite. Ntishobora gusubizwa.",
    "mods.deleting":         "Gusiba…",
    "mods.moduleCount":      "Amamodule {n}",
    "mods.code":             "Code",
    "mods.backToModules":    "← Amamodule",
    "mods.failedToLoad":     "Byanze gushyira module",
    "mods.completionsCTA":   "Abigishwa barangije",
    "mods.avgRating":        "Amanota y'hagati",
    "mods.outOf50":          "kuri 5.0",
    "mods.attachments":      "inyandiko zifatanye",
    "mods.videosResources":  "Amavidewo & Ibyunguriro",
    "mods.editResources":    "✏️ Hindura ibyunguriro",
    "mods.noVideosYet":      "Nta mavidewo",
    "mods.totalVideos":      "amavidewo yose",

    // ── Amashusho ────────────────────────────────────────────────────────────
    "videos.title":          "Gucunga amashusho",
    "videos.addVideo":       "Ongeraho videyo",
    "videos.allVideos":      "Amashusho yose",
    "videos.noVideosMatch":  "Nta mashusho ahuye n'inyuzuzanyo zawe",
    "videos.createVideo":    "Shiraho videyo",
    "videos.editVideo":      "Hindura videyo",
    "videos.details":        "🎬 Ibisobanuro by'amavidewo",
    "videos.videoEnded":     "Videwo irarangiye",
    "videos.replay":         "▶ Subiramo",
    "videos.module":         "📦 Module",
    "videos.duration":       "⏱ Igihe",
    "videos.added":          "📅 Byongewemo",
    "videos.idLabel":        "🆔 ID",
    "videos.publish":        "📣 Tangaza",
    "videos.unpublish":      "📝 Hagarika",
    "videos.editBtn":        "✏️ Hindura",
    "videos.archive":        "🗑 Binyambare",
    "videos.unknown":        "Ntizwi",
    "videos.noVideosYet":    "Nta mavidewo",

    // ── Makamakuru ───────────────────────────────────────────────────────────
    "anns.title":            "Shiraho itangazo",
    "anns.announcementTitle":"Inyito",
    "anns.message":          "Ubutumwa",
    "anns.targetAudience":   "Abagenerwabikorwa",
    "anns.saveDraft":        "💾 Bika igenamigambi",
    "anns.publishNow":       "📣 Tangaza ubu",
    "anns.confirmPublish":   "📣 Emeza gutangaza",
    "anns.yesPublish":       "📣 Yego, tangaza ubu",
    "anns.statistics":       "Imibare y'amatangazo",
    "anns.livePreview":      "📱 Reba mbere live",
    "anns.totalSent":        "Byoherejwe byose",
    "anns.pushDelivered":    "Push yakiriwe",
    "anns.bannerViews":      "Ibyo bona ibango",
    "anns.dismissals":       "Ibyahakanye",
    "anns.noAnnouncements":  "Nta matangazo kugeza ubu",
    "anns.clickToViewWhoSaw":"Kanda umurongo kureba ubabonye iri tangazo",
    "anns.details":          "📣 Ibisobanuro by'itangazo",
    "anns.pushSent":         "Push yoherejwe",
    "anns.dismissed":        "Byashyiriweho",
    "anns.openRate":         "Igipimo cy'ifungura",
    "anns.created":          "Byakozwe",
    "anns.publishedDate":    "Byatangazwe",
    "anns.draftOnly":        "Agateganyo gusa",
    "anns.whoSawThis":       "Nde wabirebe",
    "anns.whoViewed":        "👁 Nde wabirebe",
    "anns.noViewData":       "Nta makuru y'ibirebwa",
    "anns.viewedAt":         "Birebwe kuri",
    "anns.sentTitle":        "📣 Amatangazo yoherejwe",
    "anns.bannerViewsTitle": "👁 Ibyo bona ibango",
    "anns.pushDeliveredTitle":"📊 Push yakiriwe",
    "anns.dismissalsTitle":  "✅ Ibyahakanye",
    "anns.audience":         "Abagenerwabikorwa",
    "anns.dismissedBy":      "Byashyiriweho na",
    "anns.sentDesc":         "Amatangazo yose yatangazwe n'uko push yageze.",
    "anns.bannerDesc":       "Amatangazo n'utigeze ibango rirebwa inshuro.",
    "anns.pushDesc":         "Umubare w'amatangazo ya push yakiriwe ku tangazo rimwe rimwe.",
    "anns.dismissalsDesc":   "Abakoresha bafashe cyangwa bahakanye ibango ry'urubuga.",

    // ── Amasubscription ──────────────────────────────────────────────────────
    "subs.title":            "Amasubscription ({n})",
    "subs.allPlatforms":     "Imbuga zose",
    "subs.allStatus":        "Imimerere yose",
    "subs.allPlans":         "Gahunda zose",
    "subs.noMatch":          "Nta subscription ihuye n'inyuzuzanyo zawe",
    "subs.restore":          "Subira aho",
    "subs.cancel":           "Hagarika",
    "subs.filtering":        "● gutunganya",
    "subs.clickToFilter":    "kanda gutunganya",
    "subs.details":          "💳 Ibisobanuro by'ubusubire",
    "subs.periodStart":      "Intangiriro y'igihe",
    "subs.periodEnd":        "Iherezo ry'igihe",
    "subs.cancelledAt":      "Byahagaritswe ku",
    "subs.mobileProvider":   "Gutanga kuri telefone",
    "subs.mobilePhone":      "Nimero ya telefone",
    "subs.restoreBtn":       "✅ Subira amasubscription",
    "subs.cancelBtn":        "Hagarika ubusubire",

    // ── Baza Dr. Gad ─────────────────────────────────────────────────────────
    "askgad.pending":        "Bitegereje",
    "askgad.underReview":    "Birashakishwa",
    "askgad.responded":      "Byasubijwe",
    "askgad.total":          "Byose",
    "askgad.allSubmissions": "ibyo byose byoherejwe",
    "askgad.awaitingResponse":"bitegereje igisubizo",
    "askgad.beingReviewed":  "birashakishwa",
    "askgad.completed":      "byarangiye",
    "askgad.noMatch":        "Nta byo byoherejwe bihuye n'inyuzuzanyo zawe",
    "askgad.submissionDetails":"🎤 Ibisobanuro by'icyoherejwe",
    "askgad.respondTitle":   "✍️ Subiza ikibazo",
    "askgad.writeResponse":  "Andika igisubizo",
    "askgad.yourResponse":   "Igisubizo cyawe",
    "askgad.pushNoteSent":   "(bizabikwa + itangazo rya push rizohererezwa)",
    "askgad.sendResponse":   "Ohereza igisubizo + itangazo rya push",
    "askgad.drGadResponse":  "Igisubizo cya Dr. Gad",
    "askgad.writeResponseBtn":"✍️ Andika igisubizo",
    "askgad.details":        "Ibisobanuro",
    "askgad.respond":        "✍️ Subiza",

    // ── Inkunga ──────────────────────────────────────────────────────────────
    "donations.title":           "Inkunga ({n})",
    "donations.totalReceived":   "Byose byakiriwe",
    "donations.scholarships":    "Buruse",
    "donations.pending":         "Bitegereje",
    "donations.methods":         "Uburyo",
    "donations.noYet":           "Nta nkunga kugeza ubu",
    "donations.details":         "💝 Ibisobanuro by'inkunga",
    "donations.scholarshipDetails":"🎓 Ibisobanuro bya buruse",
    "donations.donorWall":       "Urubura rw'abatanga",
    "donations.visible":         "✅ Biragaragara",
    "donations.private":         "🔒 Ibanga",
    "donations.scholarshipGranted":"Buruse yahawe",
    "donations.grantedOn":       "Yahawe ku wa",
    "donations.notes":           "Ibisobanuro",
    "donations.userId":          "ID y'umutumiwa",
    "donations.activeLabel":     "Birakora",
    "donations.email":           "Imeli",
    "donations.activeScholarships":"Buruse birakora",
    "donations.donationsByMethod":"💳 Inkunga hakurikijwe uburyo",
    "donations.confirmGrant":    "🎓 Emeza gutanga buruse",
    "donations.grantByEmail":    "Tanga hakoreshejwe imeli",
    "donations.grant":           "Tanga",
    "donations.confirmGrantBtn": "✅ Emeza gutanga",
    "donations.totalReceivedLabel":"Byose byakiriwe",

    // ── Amafaranga kuri telefone ─────────────────────────────────────────────
    "mm.title":              "Imirimo ({n})",
    "mm.providerBreakdown":  "Ibisobanuro hakurikijwe umutuza",
    "mm.total":              "Byose",
    "mm.active":             "Birakora",
    "mm.pending":            "Bitegereje",
    "mm.failed":             "Byanze",
    "mm.allProviders":       "Abatuza bose",
    "mm.allStatus":          "Imimerere yose",
    "mm.allPlans":           "Gahunda zose",
    "mm.noMatch":            "Nta mirimo ihuye n'inyuzuzanyo zawe",
    "mm.transactionDetails": "📱 Ibisobanuro by'imirimo",
    "mm.activate":           "⚡ Kora",
    "mm.manualActivate":     "⚡ Gukora ubusubire mu buryo bw'intoki",
    "mm.provider":           "Uruhinja",
    "mm.phone":              "Telefoni",
    "mm.txRef":              "Indangakimwe ya Tx",
    "mm.userEmail":          "Imeli y'umutumiwa",

    // ── Imitwe y'imbonerahamwe ───────────────────────────────────────────────
    "tbl.title":             "Umutwe",
    "tbl.type":              "Ubwoko",
    "tbl.source":            "Inkomoko",
    "tbl.duration":          "Igihe",
    "tbl.added":             "Byongewemo",
    "tbl.actions":           "Ibikorwa",
    "tbl.storage":           "Aho kubikwa",
    "tbl.description":       "Ibisobanuro",
    "tbl.code":              "Code",
    "tbl.videos":            "Amavidewo",
    "tbl.preview":           "Kureba",
    "tbl.completions":       "Ibyarangiye",

    // ── Igiciro ──────────────────────────────────────────────────────────────
    "pricing.title":         "Igiciro cy'ubusubire",
    "pricing.subtitle":      "Gucunga ibiciro, igihe cy'ikizamini n'igenamiterere ry'ubwishyu",
    "pricing.monthlyPlan":   "Gahunda y'ukwezi",
    "pricing.annualPlan":    "Gahunda y'umwaka",
    "pricing.freeTrial":     "Ikizamini cy'ubuntu",
    "pricing.localCurrency": "Ifaranga rya gaferi",
    "pricing.localCountry":  "Igihugu",
    "pricing.refresh":       "Vugurura",
    "pricing.retry":         "Ongera ugerageze",
    "pricing.notSet":        "ntabwo gushyirwa",
    "pricing.editBtn":       "✏️ Hindura",
    "pricing.editTitle":     "✏️ Hindura {label}",
    "pricing.saveBtn":       "✅ Bika",

    // ── Kwishyura na karita ──────────────────────────────────────────────────
    "cardpay.title":         "Kwishyura na karita — Stripe",
    "cardpay.subtitle":      "Amasubscription ya karita ya Stripe n'ibikoresho byo gukurikirana ibikorwa",
    "cardpay.total":         "Byose",
    "cardpay.active":        "Birakora",
    "cardpay.trial":         "Ikizamini",
    "cardpay.cancelled":     "Byahagaritswe",
    "cardpay.webhookHandlers":"Ibikoresho bya webhook ya Stripe",
    "cardpay.allActive":     "Byose birakora",
    "cardpay.stripePaymentDetail":"Ibisobanuro by'ubwishyu bwa Stripe",
    "cardpay.stripeSubscriptions":"Amasubscription ya Stripe ({n})",
    "cardpay.noStripeFound": "Nta masubscription ya Stripe yabonetse",
    "cardpay.allStripeSubs": "amasubscription yose ya Stripe",
    "cardpay.payingSubscribers":"abasubscriber ba mbwirabuzi",
    "cardpay.inFreeTrial":   "mu ikizamini cy'ubuntu",
    "cardpay.churned":       "bahagaritse",
    "cardpay.stripeSubId":   "ID y'amasubscription ya Stripe",
    "cardpay.stripeCustomer":"Umukiriya wa Stripe",

    // ── Imimerere ────────────────────────────────────────────────────────────
    "status.active":       "Birakora",
    "status.published":    "Byatangazwe",
    "status.draft":        "Igenamigambi",
    "status.archived":     "Binyambare",
    "status.trial":        "Ikizamini",
    "status.cancelled":    "Byahagaritswe",
    "status.expired":      "Byararangiye",
    "status.pending":      "Bitegereje",
    "status.scholarship":  "Buruse",
    "status.completed":    "Byarangiye",
    "status.failed":       "Byanze",
    "status.responded":    "Byasubijwe",
    "status.none":         "Nta na kimwe",

    "filter.all":          "Byose",

    "subs.statusActive":      "✅ Birakora",
    "subs.statusTrial":       "🟡 Ikizamini",
    "subs.statusCancelled":   "❌ Byahagaritswe",
    "subs.statusExpired":     "🕐 Byararangiye",
    "subs.statusScholarship": "🎓 Buruse",
    "subs.planMonthly":       "Ukwezi",
    "subs.planAnnual":        "Umwaka",
    "subs.planScholarship":   "Buruse",
    "subs.childProfile":      "🧒 Umwana",

    "askgad.filterReview":        "🔍 Gusuzuma",
    "askgad.filterDone":          "✅ Byarangiye",
    "askgad.statusSubmitted":     "⏳ Bitegereje",
    "askgad.statusUnderReview":   "🔍 Birashakishwa",
    "askgad.statusResponded":     "✅ Byasubijwe",
    "askgad.responsePlaceholder": "Andika igisubizo cyuzuye kuri umubyeyi uyu…",

    "mods.allStatus":       "Imimerere yose",
    "mods.statusPublished": "✅ Byatangazwe",
    "mods.statusDraft":     "📝 Igenamigambi",
    "mods.statusArchived":  "🗃 Binyambare",

    "videos.allTypes":          "Ubwoko bwose",
    "videos.typeIntro":         "Intangiriro",
    "videos.typePractice":      "Imyitozo",
    "videos.typeContent":       "Ibikubiye",
    "videos.typeExplanation":   "Ibisobanuro",
    "videos.typeTaster":        "Ikizamini",
    "videos.typeDemonstration": "Igerageza",
    "videos.typeAssessment":    "Isuzuma",
    "videos.selectModule":      "Hitamo module…",
    "videos.selectModuleDash":  "— Hitamo module —",
    "videos.previewClipLabel":  "Agasoro k'ubuntu",
    "videos.ytFetching":        "Kuzuzanya biva YouTube…",
    "videos.titlePlaceholder":  "urugero. Intangiriro y'iterambere ry'ijwi",

    "anns.previewNow":           "ubu",
    "anns.titleInputPh":         "Inyito y'itangazo…",
    "anns.messageInputPh":       "Ababyeyi bazabona ibi nk'itangazo rya push n'ibango ry'urubuga…",
    "anns.previewTitlePh":       "Inyito y'itangazo ryawe",
    "anns.previewBodyPh":        "Ubutumwa bwawe buzagaragara hano…",
    "anns.inAppBanner":          "Ibango ry'urubuga",
    "anns.bannerTitlePh":        "Inyito",
    "anns.bannerBodyPh":         "Reba ubutumwa",
    "anns.target":               "Intego",
    "anns.viewersHeader":        "Abareba",
    "anns.targetAllSubscribers": "Abasubscriber bose",
    "anns.targetActiveOnly":     "Birakora gusa",
    "anns.targetSpeechModule":   "Abakoresha ba module y'ijwi",
    "anns.targetAllRegistered":  "Bose biyandikishije",
    "anns.subAnnouncements":     "amatangazo yakozwe",
    "anns.subPushNotifs":        "amatangazo ya push yose",
    "anns.subBannerViews":       "ibyo bona ibango ry'urubuga",
    "anns.subConfirmedViews":    "ibyo bona ibango byemejwe",
    "anns.publishWarning":       "Ibi bizohererezwa ubutumwa bwa push ku bakoresha bose bahuye. Ibi ntishobora gusubirwaho.",
    "anns.confirmTarget":        "Intego: {target}",
    "anns.titleRequired":        "Inyito n'ubutumwa bikenewe",
    "anns.draftSaved":           "✅ Igenamigambi ryabitswe!",
    "anns.publishedToast":       "📣 Itangazo ryatangazwe!",

    "donations.donationsCardTitle":"Inkunga ({n})",
    "donations.scholarshipsTitle": "Buruse ({n})",
    "donations.filterAll":         "Byose",
    "donations.filterCompleted":   "Byarangiye",
    "donations.filterPending":     "Bitegereje",
    "donations.filterFailed":      "Byanze",
    "donations.noScholarshipsYet": "Nta buruse kugeza ubu",
    "donations.noData":            "Nta makuru",
    "donations.activeBadge":       "birakora",
    "donations.grantedLabel":      "Yahawe ku wa {date}",
    "donations.scholarshipsCount": "Buruse {n} zirakora — ubuhabwa bwa subscriptions buratangwa ubuntu.",
    "donations.donationsCount":    "Inkunga {n}",

    "pricing.labelMonthlyUsd":   "Igiciro cy'ukwezi (USD)",
    "pricing.descMonthlyUsd":    "Igiciro cy'ubusubire bwa buri kwezi (USD)",
    "pricing.labelAnnualUsd":    "Igiciro cy'umwaka (USD)",
    "pricing.descAnnualUsd":     "Igiciro cy'ubusubire bw'umwaka (USD)",
    "pricing.labelMonthlyLocal": "Igiciro cy'ukwezi (gaferi)",
    "pricing.descMonthlyLocal":  "Igiciro cy'ukwezi mu ifaranga rya gaferi",
    "pricing.labelAnnualLocal":  "Igiciro cy'umwaka (gaferi)",
    "pricing.descAnnualLocal":   "Igiciro cy'umwaka mu ifaranga rya gaferi",
    "pricing.labelCurrencyLocal":"Code y'ifaranga rya gaferi",
    "pricing.descCurrencyLocal": "Code ISO y'ifaranga (urugero. KES, NGN)",
    "pricing.labelLocalCountry": "Igihugu cy'abakiriya",
    "pricing.descLocalCountry":  "Igihugu nyamukuru cy'isoko",
    "pricing.labelTrialDays":    "Iminsi y'ikizamini cy'ubuntu",
    "pricing.descTrialDays":     "Iminsi yo kugera nta bwishyu mbere yo gutangira kwishyura",
    "pricing.labelStripeMonthly":"ID y'igiciro cya Stripe (ukwezi)",
    "pricing.descStripeMonthly": "Price ID ya Stripe yo ku gahunda y'ukwezi",
    "pricing.labelStripeAnnual": "ID y'igiciro cya Stripe (umwaka)",
    "pricing.descStripeAnnual":  "Price ID ya Stripe yo ku gahunda y'umwaka",
    "pricing.groupSubscription": "Igiciro cy'ubusubire",
    "pricing.groupTrial":        "Igenamiterere ry'ikizamini",
    "pricing.groupStripe":       "Ubuteranye na Stripe",
    "pricing.currency":          "Ifaranga: {code}",
    "pricing.updated":           "Vugururiwe ku wa {date}",
    "pricing.priceHint":         "💡 Injiza umubare gusa (urugero. 14.00 ya $14). Ikimenyetso cy'ifaranga ntikenewe.",
    "pricing.thenCharged":       "Hanyuma buri kwezi",
    "pricing.usdStripe":         "USD · Stripe / Flutterwave",

    "acct.mfa":             "Kwemeza inshuro ebyiri (2FA)",
    "acct.mfaHint":         "Ongeraho umutekano kurubuga rwawe. Iyo byakozwe, uzahabwa code yo gukoreshwa rimwe buri gihe winjira.",
    "acct.mfaEnabled":      "2FA irakoreshwa kuri konti yawe",
    "acct.mfaDisabled":     "2FA ntirakoreshwa ubu",
    "acct.enableMfa":       "Kora 2FA",
    "acct.disableMfa":      "Hagarika 2FA",
    "acct.sendOtp":         "Ohereza code",
    "acct.sendingOtp":      "Kohereza…",
    "acct.otpCode":         "Code yo kwemeza",
    "acct.otpPlaceholder":  "Injiza code y'imibare 6",
    "acct.verifyOtp":       "Emeza & Kora",
    "acct.verifyingOtp":    "Kwemeza…",
    "acct.otpSent":         "✅ Code yoherejwe kuri {dest}",
    "acct.mfaEnabledToast": "✅ Kwemeza inshuro ebyiri byakozwe!",
    "acct.mfaDisabledToast":"2FA yahagaritswe",

    // ── IMPAPURO Z'AMATEGEKO ─────────────────────────────────────────────────

    "crumb.privacy":  "Politiki y'Ibanga",
    "crumb.terms":    "Amabwiriza y'Imikorere",
    "crumb.support":  "Inkunga",
    "search.privacy": "Shakisha politiki…",
    "search.terms":   "Shakisha amabwiriza…",
    "search.support": "Shakisha inkunga…",

    "footer.privacy": "Ibanga",
    "footer.terms":   "Amabwiriza",
    "footer.support": "Inkunga",

    "legal.lastUpdated":   "Ivugururiwe",
    "legal.jurisdiction":  "Ubutware",
    "legal.dataStandard":  "Ingingo z'Amakuru",
    "legal.effectiveDate": "Itariki Itangirira",
    "legal.governingLaw":  "Itegeko Rigenga",
    "legal.version":       "Verisiyo",
    "legal.responseTime":  "Igihe cy'Igisubizo",
    "legal.supportHours":  "Amasaha y'Inkunga",
    "legal.priority":      "Inzira",
    "legal.lastUpdatedVal": "24 Gicurasi 2026",
    "legal.rwandaVal":      "Republika y'u Rwanda",
    "legal.gdprVal":        "Ihurira na GDPR",
    "legal.responseVal":    "Munsi y'amasaha 24",
    "legal.hoursVal":       "Ku wa 1–5, 08:00–18:00",
    "legal.priorityVal":    "Ibibazo bikomeye: amasaha 2",

    // ── POLITIKI Y'IBANGA ────────────────────────────────────────────────────
    "privacy.title":    "Politiki y'Ibanga",
    "privacy.subtitle": "Ivugururiwe: Gicurasi 2026 · Itangira ako kanya nyuma yo kwemeza",

    "privacy.s1.title": "1. Turi Bande",
    "privacy.s1.p1":    "Kunga Basics Ltd. (« twebwe ») igenga Portali y'Ubuyobozi wa Kunga Basics n'urubuga rw'kwiga rwawo. Twanditse mu Republika y'u Rwanda kandi twiyemeje kurinda amakuru bwite y'abakoresha bose, abayobozi, n'abongereza bakorana n'serivisi zacu.",
    "privacy.s1.p2":    "Iyi Politiki y'Ibanga isobanura uburyo twegeranya, dukoresha, dubyaza n'kurinda amakuru bwite iyo ukoresha Portali y'Ubuyobozi wa Kunga Basics. Winjira muri iyi portali bivuze ko wasomye kandi wumvise iyi politiki.",

    "privacy.s2.title": "2. Amakuru Twegeranya",
    "privacy.s2.lead":  "Twegeranya ibi bice by'amakuru bwite:",
    "privacy.s2.i1":    "Amakuru y'konti: izina ryuzuye, aderesi imeli, ifoto ya profil, n'akazi wahawe.",
    "privacy.s2.i2":    "Amakuru yo kwinjira: hashi z'ijambo ry'ibanga (ntabwo ari umwandiko), indangano za Google OAuth.",
    "privacy.s2.i3":    "Inyandiko z'ibikorwa: igihe cyo kwinjira, aderesi IP, uburyo bwa user-agent ku bigerageza bya security.",
    "privacy.s2.i4":    "Igenamiterere: imiterere y'interface, ururimi ruhisemo, ibigengwa na MFA.",
    "privacy.s2.i5":    "Ibikorwa ku bikubiye: ibice byakozwe/bigezweho, amashusho yapakiwe, makamakuru yatangazwe.",

    "privacy.s3.title": "3. Uburyo Dukoresha Amakuru Yawe",
    "privacy.s3.lead":  "Amakuru yawe akoreshwa gusa mu bipimo bikurikira:",
    "privacy.s3.i1":    "Kwemeza ubuhamya bwawe no kubungabunga umutekano w'inyigisho.",
    "privacy.s3.i2":    "Guhuza uburambe bwawe bwa portali (imiterere, ururimi, amatangazo).",
    "privacy.s3.i3":    "Gukora inyandiko z'isuzuma ryo mu nzu ku birebana na compliance n'iperereza rya security.",
    "privacy.s3.i4":    "Kohereza amatangazo ya sisitemu, imeli zo gusubiranya ijambo ry'ibanga, n'iburyo bwa plateforme.",
    "privacy.s3.i5":    "Kunoza ubwizigame bwa plateforme binyuze mu isesengura ry'ibikorwa ritarakwanye amakuru bwite.",
    "privacy.s3.note":  "Ntidutanga, ntituzura, kandi ntidusangira amakuru yawe bwite na ba gatatu mu nzira zo gutangaza cyangwa gusakaza.",

    "privacy.s4.title": "4. Ububiko n'Kubungabunga Amakuru",
    "privacy.s4.p1":    "Amakuru yose abikwa ku makina ya PostgreSQL agemanwa na Supabase hamwe na encryption y'inzira yose (TLS 1.3) no mu bwisanzure (AES-256). Ibikoresho bya media (amafoto ya profil, amashusho y'amasomo) abikwa kuri MinIO object storage hamwe na controls z'uburenganzira bwa bucket bwite.",
    "privacy.s4.p2":    "Inyandiko z'ibikorwa zibungabungwa amezi 12. Amakuru y'konti abungabungwa mu gihe cy'akazi/amasezerano hiyongereyeho iminsi 90 nyuma yo gufunga konti. Ushobora gusaba gusibwa mbere y'igihe utumanahana na nyir'ubuyobozi wa plateforme.",

    "privacy.s5.title": "5. Uburenganzira Bwawe",
    "privacy.s5.lead":  "Nk'umuntu ufite amakuru, ufite uburenganzira bwo:",
    "privacy.s5.i1":    "Kubona: gusaba kopi y'amakuru yose bwite tugutezeko.",
    "privacy.s5.i2":    "Gutunga: gutunga amakuru atuzuye cyangwa ageze kuri Account Settings.",
    "privacy.s5.i3":    "Gusiba: gusaba gusibwa kwa konti yawe n'amakuru yihuriweho.",
    "privacy.s5.i4":    "Gutwara: guhabwa amakuru yawe mu buryo busobanuka na mashini.",
    "privacy.s5.i5":    "Guhanura: guhanura ibikorwa bya gushyira mu bikorwa by'ibihe.",
    "privacy.s5.i6":    "Kugabanya: gusaba ko tugabanya uburyo dukoresha amakuru yawe.",

    "privacy.s6.title": "6. Ingamba za Security",
    "privacy.s6.i1":    "Amagambo y'ibanga ahurizwa hakoreshejwe bcrypt n'igipimo cy'akazi cya 12.",
    "privacy.s6.i2":    "Tokens za JWT zifite igihe gito (injira: iminota 15, gusubiranya: iminsi 7).",
    "privacy.s6.i3":    "Kwemeza inshuro ebyiri (TOTP) biraboneka ku konti zose z'abayobozi.",
    "privacy.s6.i4":    "Itumanahano ryose rya API ryambukiranya HTTPS.",
    "privacy.s6.i5":    "Ibyageragejwe byo kwinjira bikorwaho inyandiko hamwe na IP na user-agent kugira ngo bisuzumwe neza.",

    "privacy.s7.title":   "7. Twunganire",
    "privacy.s7.p1":      "Ku bibazo byose bijyanye n'ibanga, gusaba kubona amakuru, cyangwa ibibarafu, tumanahana na Umuyobozi w'Uburenganzira bw'Amakuru kuri:",
    "privacy.s7.contact": "Kunga Basics Ltd. — Kurinda Amakuru\n📧 privacy@kungabasics.com\n📍 Kigali, Republika y'u Rwanda",

    // ── AMABWIRIZA Y'IMIKORERE ───────────────────────────────────────────────
    "terms.title":    "Amabwiriza y'Imikorere",
    "terms.subtitle": "Ivugururiwe: Gicurasi 2026 · Soma neza mbere y'ukoresha iyi portali",

    "terms.s1.title": "1. Kwemera Amabwiriza",
    "terms.s1.p1":    "Winjira cyangwa ukoresha Portali y'Ubuyobozi wa Kunga Basics (« Portali »), wemera gukomeza ibibwiriza by'aya Mabwiriza y'Imikorere (« Amabwiriza »). Niba utemeye aya Mabwiriza, ntukwiriye gukoresha cyangwa winjira muri Portali.",
    "terms.s1.p2":    "Aya Mabwiriza yubahirizwa n'abayobozi bose, abagenzuzi ba content, n'abakozi b'inkunga baguwe uburenganzira bwo kwinjira muri Portali na Kunga Basics Ltd.",

    "terms.s2.title": "2. Kwinjira n'Inshingano z'Konti",
    "terms.s2.lead":  "Konti yawe ikugurana uburenganzira bwo kujya muri Portali mu izina rya Kunga Basics Ltd. Wemera:",
    "terms.s2.i1":    "Kubika ibanga ry'injira ry'ibanga neza kandi kutarungika na n'umwe.",
    "terms.s2.i2":    "Kumenyesha vuba nyir'ubuyobozi w'uburyo bwa sisitemu niba usuzuma ko hari uwinjiye utabiherewe.",
    "terms.s2.i3":    "Gukoresha Portali gusa ku bihugu bya administration n'imikorere ikwiye.",
    "terms.s2.i4":    "Kubungabunga ukuri kw'amakuru ya profil yawe igihe cyose.",
    "terms.s2.i5":    "Gukora MFA niba bisabwa na politiki y'umuryango wawe.",
    "terms.s2.i6":    "Gusohoka mu nyigisho zikorwa igihe ukoresha ibikoresho byasangirwa cyangwa bya rubanda.",

    "terms.s3.title": "3. Imikorere Yemewe",
    "terms.s3.lead":  "Ushobora gukoresha Portali kugira ngo:",
    "terms.s3.i1":    "Gucunga ibice by'kwiga, amashusho, n'ibikubiye by'uburezi.",
    "terms.s3.i2":    "Gucunga konti z'abakoresha n'ingengo y'subscription.",
    "terms.s3.i3":    "Kureba isesengura, raporo z'amafaranga, n'ingeno z'imikorere.",
    "terms.s3.i4":    "Gutangaza makamakuru no gucunga itumanahano rya plateforme.",
    "terms.s3.i5":    "Kugena igenamiterere rya sisitemu mu nzego zawe z'akazi.",

    "terms.s4.title": "4. Ibikorwa Biburiwe",
    "terms.s4.lead":  "Ntukwiriye gukoresha Portali kugira ngo:",
    "terms.s4.i1":    "Kugera konti, sisitemu, cyangwa amakuru atari muri nzego z'uburenganzira bwawe.",
    "terms.s4.i2":    "Gupakira code mbi, virusi, cyangwa ibikubiye bitera ingorane.",
    "terms.s4.i3":    "Gukusanya, kongera, cyangwa gukusanyiriza amakuru y'abakoresha nta ruhushya.",
    "terms.s4.i4":    "Gukoresha Portali gutoteza, gutisha, cyangwa gukomeretsa umuntu uwo ari we wese.",
    "terms.s4.i5":    "Kugerageza gukemura, gusesengura, cyangwa gukira ingamba zose za security.",
    "terms.s4.i6":    "Kwinjira muri Portali hakoreshejwe scripts z'ubwihindurize, bots, cyangwa agents batari abantu.",
    "terms.s4.i7":    "Sangira, gurisha, cyangwa gutangaza amakuru y'ibanga ya plateforme ku bantu batahabwa ruhushya.",

    "terms.s5.title": "5. Uburenganzira bw'Iterambere",
    "terms.s5.p1":    "Ibikubiye byose, softwares, trademarks, n'ibikoresho bibonetse binyuze muri Portali ni umutungo w'ibanze wa Kunga Basics Ltd. cyangwa abamuha lisansi. Uhabwa lisansi igabanyije, itari iy'umwe gusa, idahurizwa kugira ngo ukoreshe Portali gusa ku bikorwa by'administration.",
    "terms.s5.p2":    "Ntushobora gukopya, kongera, gutangaza, cyangwa gukora imirimo ivuye mu bikubiye bya Portali nta ruhushya rw'inyandiko mbere biva kuri Kunga Basics Ltd.",

    "terms.s6.title": "6. Kugabanya Inshingano",
    "terms.s6.p1":    "Mu rugero rw'amategeko yubahirizwa, Kunga Basics Ltd. ntizabazwa ingaruka zose z'inzira, za kwa kamere, zidasanzwe, z'inkomoko, cyangwa zo guhanwa bivuye mu gukoresha cyangwa kutashobora gukoresha Portali, harimo gutakaza amakuru, guhagarika imikorere, cyangwa ingaruka ku izina.",
    "terms.s6.p2":    "Kunga Basics Ltd. ntizerekanya imikorere idahagarara cyangwa idafite amakosa ya Portali kandi igumana uburenganzira bwo gukora maintenance cyangwa kuvugurura igihe cyose.",

    "terms.s7.title": "7. Guhagarika",
    "terms.s7.p1":    "Kunga Basics Ltd. igumana uburenganzira bwo guhagarika cyangwa guha burundu uburenganzira bwawe bwo kwinjira muri Portali vuba kandi nta makuru niba utubahirije aya Mabwiriza cyangwa ukora ibikorwa bibangamira plateforme, abakoresha bayo, cyangwa umuryango.",
    "terms.s7.p2":    "Nyuma yo guhagarika, uburenganzira bwawe bwo kwinjira muri Portali burangira ako kanya kandi amakuru yose ahuriweho na konti yawe ashobora kubikwa cyangwa gusibwa hakurikijwe Politiki y'Ibanga.",

    "terms.s8.title": "8. Guhindura Amabwiriza",
    "terms.s8.p1":    "Tugumana uburenganzira bwo guhindura aya Mabwiriza igihe cyose. Impinduka nkuru zizamenyeshwa binyuze mu matangazo ya plateforme cyangwa imeli. Gukomeza gukoresha Portali nyuma y'impinduka nk'izo bivuze ko wemeye Amabwiriza avuguruwe.",

    "terms.s9.title":   "9. Itegeko Rigenga",
    "terms.s9.p1":      "Aya Mabwiriza agemanwa kandi asobanurwa hakurikijwe amategeko ya Republika y'u Rwanda. Impaka zose zavuka muri aya Mabwiriza zizagirwa mu nkiko z'u Rwanda gusa.",
    "terms.s9.contact": "Kunga Basics Ltd. — Amategeko\n📧 legal@kungabasics.com\n📍 Kigali, Republika y'u Rwanda",

    // ── INKUNGA ──────────────────────────────────────────────────────────────
    "support.title":    "Inkunga",
    "support.subtitle": "Bona ubufasha, soma ibibazo bikunze kubazwa, cyangwa tutumanahane — turi hano kukufasha",
    "support.faqTitle": "Ibibazo Bikunze Kubazwa",
    "support.formTitle":"Tutumanahane",

    "support.email.title":  "Inkunga ya Imeli",
    "support.email.val":    "support@kungabasics.com",
    "support.email.sub":    "Ikwiye ku bibazo bitari bya vuba",
    "support.email.action": "Ohereza imeli",
    "support.tech.title":   "Ibibazo bya Tekinoloji",
    "support.tech.val":     "tech@kungabasics.com",
    "support.tech.sub":     "Bugs, ihagarara, ibibazo by'amakuru",
    "support.tech.action":  "Ohereza imeli",
    "support.docs.title":   "Ibisobanuro",
    "support.docs.val":     "Umuyoboro w'Ubuyobozi",
    "support.docs.sub":     "Ibisobanuro byuzuye bya plateforme",
    "support.docs.action":  "Fungura ibisobanuro",

    "support.faq.q1": "Nigute subiranya ijambo ry'ibanga?",
    "support.faq.a1": "Kanda 'Wibagiwe ijambo ry'ibanga' ku ipaji yo kwinjira, injiza aderesi yawe y'imeli hanyuma ukurikire link yoherejwe muri inbox yawe. Iyi link irarangira nyuma y'isaha 1.",
    "support.faq.q2": "Nigute kora kwemeza inshuro ebyiri (MFA)?",
    "support.faq.a2": "Jya muri Igenamiterere ry'Konti → Umutekano → Kwemeza inshuro ebyiri hanyuma uhindure. OTP izasabwa buri gihe winjira nyuma yo gukora.",
    "support.faq.q3": "Nigute gutangaza igice cy'kwiga?",
    "support.faq.a3": "Jya muri Ibikubiye → Ibice, kanda '+ Igice gishya', uzuza amakuru asabwa n'ibikubiye, hanyuma uhindure inzira ku 'Yatangazwe' hakoreshejwe menu.",
    "support.faq.q4": "Ni iki gituma ntabona ibintu bimwe bya menu?",
    "support.faq.a4": "Akazi kawe k'ubuyobozi kashobora kuba kagabanyije uburenganzira ku bice bimwe. Tumanahana na nyir'ubuyobozi w'uburyo bwa sisitemu kugira ngo basuzume uburenganzira bwawe. Ushobora nanone gusubiza ibintu byahishe muri sidebar ukanda 'Subiranya byose'.",
    "support.faq.q5": "Nigute kwishyura subscription bikorwa?",
    "support.faq.a5": "Kwishyura bikorwa binyuze kuri Mobile Money (MTN/Airtel) no kwishyura na karita (Stripe). Imirimo yose yandikwa munsi ya Inyungu → Mobile Money na Inyungu → Kwishyura na Karita.",
    "support.faq.q6": "Nigute gucunga umurongo w'Ubaza Dr. Gad?",
    "support.faq.a6": "Jya muri Abakoresha → Umurongo wa Dr. Gad. Ushobora kureba, gusubiza, no gushyira ibibazo nk'ibyakemutse. Ibibazo biragaragarira mu murongo w'igihe hamwe n'ibyo kera bitasubijwe bishyizwe ahagaragara.",
    "support.faq.q7": "Ni iki igisiga cy'amatangazo kigaragaza?",
    "support.faq.a7": "Igisiga cy'amatangazo kigaragaza amatangazo ya sisitemu, iyandikisha ry'abakoresha bashya, impinduka z'subscription, ibikubiye byashyizwe icyonyiro, n'ibindi bikorwa bya plateforme. Amatangazo atarasomwa afite counteri itukura.",
    "support.faq.q8": "Nigute guhindura ururimi rwa plateforme?",
    "support.faq.a8": "Jya muri Igenamiterere ry'Konti → Ibihitamo hanyuma uhitemo ururimi uhitemo. Portali ishyigikira Icyongereza, Igifaransa, na Kinyarwanda. Ihitamo ryawe ribikwa kuri konti yawe.",

    "support.form.subject":     "Insanganyamatsiko",
    "support.form.subjectHint": "Ibisobanuro bigufi by'ikibazo cyawe…",
    "support.form.message":     "Ubutumwa",
    "support.form.messageHint": "Sobanura ikibazo cyawe neza. Shyiramo inzira zo kongera niba wavuga bug…",
    "support.form.send":        "Ohereza Ubutumwa →",
    "support.sent.title":       "Ubutumwa Bwoherejwe!",
    "support.sent.body":        "Twabonye ubutumwa bwawe kandi tuzagusubiza munsi y'amasaha 24.",
  },
};

// ─── TRANSLATE HELPER ─────────────────────────────────────────────────────────

/**
 * Translate a key for a given language.
 * Falls back to English if the key is missing in the target language.
 * Supports simple variable substitution: t("acct.uploading", { pct: 42 }) → "Uploading 42%…"
 */
export function translate(lang, key, vars = {}) {
  const dict = translations[lang] ?? translations.en;
  let str = dict[key] ?? translations.en[key] ?? key;
  Object.entries(vars).forEach(([k, v]) => { str = str.replace(`{${k}}`, v); });
  return str;
}

// ─── CONTEXT ──────────────────────────────────────────────────────────────────

export const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(
    () => localStorage.getItem("kb_lang") ?? "en"
  );

  const setLang = (code) => {
    setLangState(code);
    localStorage.setItem("kb_lang", code);
  };

  // Pre-curried translate bound to current lang
  const t = (key, vars) => translate(lang, key, vars);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang() must be used inside <LangProvider>");
  return ctx;
}
